import { createFileRoute } from '@tanstack/react-router';
import { Fragment, useState } from 'react';

import { fmt, fmtDate, toNumber } from '@dcc-erp/shared';
import type {
  AssetMaster,
  AssetMasterType,
  BankAccount,
  BankAccountType,
  Branch,
  ChartOfAccount,
  Company,
  CreateAssetMaster,
  CreateBankAccount,
  CreateBranch,
  CreateCurrencyMaster,
  CreateCustomer,
  CreateDepartment,
  CreateEmployee,
  CreateLoan,
  CreatePaymentTerm,
  CreateProject,
  CreateVendor,
  CreateVoucherBook,
  CurrencyMaster,
  Customer,
  Department,
  DocumentFormat,
  DocumentType,
  Employee,
  Loan,
  LoanPurpose,
  PaymentTerm,
  PeriodMaster,
  PeriodStatus,
  Project,
  ResetRule,
  UpdatePaymentTerm,
  UpdateAssetMaster,
  UpdateBankAccount,
  UpdateBranch,
  UpdateCurrencyMaster,
  UpdateCustomer,
  UpdateDepartment,
  UpdateDocumentType,
  UpdateEmployee,
  UpdateLoan,
  UpdatePeriodMaster,
  UpdateProject,
  UpdateVendor,
  UpdateVoucherBook,
  Vendor,
  VoucherBook,
  VoucherBookWithCounter,
} from '@dcc-erp/shared';
import { Icon } from '@dcc-erp/ui';
import type { IconName } from '@dcc-erp/ui';

import { Can } from '../components/Can';
import { BranchSelect, DepartmentSelect } from '../components/dimensions';
import { HelpButton } from '../components/help/HelpButton';
import { PageHead } from '../components/layout';
import {
  resolveName,
  useActiveCompanyId,
  useAssetMasters,
  useBankAccounts,
  useBranches,
  useBranchNameMap,
  useCoa,
  useCompanies,
  useCreateAssetMaster,
  useCreateBankAccount,
  useCreateBranch,
  useCreateCurrency,
  useCreateCustomer,
  useCreateDepartment,
  useCreateEmployee,
  useCreateLoan,
  useCreatePaymentTerm,
  useCreateProject,
  useCreateVendor,
  useCreateVoucherBook,
  useCurrencies,
  useCustomers,
  useDeleteAssetMaster,
  useDeleteBankAccount,
  useDeleteBranch,
  useDeleteCustomer,
  useDeleteDepartment,
  useDeleteEmployee,
  useDeactivatePaymentTerm,
  useDeleteLoan,
  useDeleteProject,
  useDeleteVendor,
  useDeleteVoucherBook,
  useDepartments,
  useDepartmentNameMap,
  useDocumentTypes,
  useEmployees,
  useLoans,
  usePaymentTerms,
  usePeriods,
  useProjects,
  useProjectNameMap,
  useUpdateAssetMaster,
  useUpdateBankAccount,
  useUpdateBranch,
  useUpdateCurrency,
  useUpdateCustomer,
  useUpdateDepartment,
  useUpdateDocumentType,
  useUpdateEmployee,
  useUpdateLoan,
  useUpdatePaymentTerm,
  useUpdatePeriod,
  useUpdateProject,
  useUpdateVendor,
  useUpdateVoucherBook,
  useVendors,
  useVoucherBooks,
} from '../lib/queries';

import type { CSSProperties } from 'react';

type MasterId =
  | 'coa'
  | 'company'
  | 'branch'
  | 'dept'
  | 'project'
  | 'employee'
  | 'vendor'
  | 'customer'
  | 'paymentterm'
  | 'bank'
  | 'asset'
  | 'loan'
  | 'doc'
  | 'period'
  | 'currency';

interface NavItem {
  id: MasterId;
  label: string;
  icon: IconName;
}

interface NavGroup {
  section: string;
  items: readonly NavItem[];
}

interface SectionTitle {
  t: string;
  s: string;
}

const MD_NAV: readonly NavGroup[] = [
  {
    section: 'หลัก',
    items: [
      { id: 'coa', label: 'ผังบัญชี', icon: 'book' },
      { id: 'company', label: 'บริษัทในเครือ', icon: 'factory' },
      { id: 'branch', label: 'สาขา', icon: 'branch' },
      { id: 'dept', label: 'หน่วยงาน / แผนก', icon: 'branch' },
      { id: 'project', label: 'Project / Cost Center', icon: 'ledger' },
    ],
  },
  {
    section: 'คน + คู่ค้า',
    items: [
      { id: 'employee', label: 'พนักงาน', icon: 'eye' },
      { id: 'vendor', label: 'ผู้ขาย (Vendor)', icon: 'ap' },
      { id: 'customer', label: 'ลูกค้า', icon: 'ar' },
      { id: 'paymentterm', label: 'เงื่อนไขชำระ (Payment Terms)', icon: 'calendar' },
    ],
  },
  {
    section: 'ทรัพยากร',
    items: [
      { id: 'bank', label: 'ธนาคารและบัญชี', icon: 'bank' },
      { id: 'asset', label: 'ทรัพย์สิน Master', icon: 'factory' },
      { id: 'loan', label: 'เงินกู้บริษัท', icon: 'ap' },
    ],
  },
  {
    section: 'ระบบ',
    items: [
      { id: 'doc', label: 'เลขเอกสาร (Sequence)', icon: 'journal' },
      { id: 'period', label: 'ปีบัญชี & งวด', icon: 'calendar' },
      { id: 'currency', label: 'สกุลเงิน & FX Rate', icon: 'tax' },
    ],
  },
];

const TITLES: Readonly<Record<MasterId, SectionTitle>> = {
  coa: {
    t: 'ผังบัญชี (Chart of Accounts)',
    s: 'รหัสบัญชี + ชื่อ + ประเภท + บัญชีระดับล่าง — รากฐานของทุกการบันทึก',
  },
  company: {
    t: 'บริษัทในเครือ',
    s: 'DCC / TTOP / RCI — แต่ละบริษัทมีเลขผู้เสียภาษีและธีมสีของตัวเอง',
  },
  branch: {
    t: 'สาขา',
    s: 'รหัสสาขา + ลำดับที่ของสาขา + ที่อยู่',
  },
  dept: {
    t: 'หน่วยงาน / แผนก',
    s: 'แผนกในบริษัท + Cost Center + จำนวนพนักงาน — ใช้ปันส่วนค่าใช้จ่าย',
  },
  project: {
    t: 'Project / Cost Center',
    s: 'โครงการ CIP, โครงการขาย, Cost Center — ติดตามต้นทุนรายโครงการ',
  },
  employee: {
    t: 'พนักงาน (Employee)',
    s: 'รายชื่อพนักงาน + แผนก + บัญชีธนาคาร — ใช้กับเงินทดรองและเงินสดย่อย',
  },
  vendor: { t: 'ผู้ขาย (Vendor)', s: 'รายชื่อคู่ค้า + เลขผู้เสียภาษี + Payment Terms + สกุลเงิน' },
  customer: {
    t: 'ลูกค้า (Customer)',
    s: 'รายชื่อลูกค้า + Credit Limit + Payment Terms + ประวัติยอดขาย',
  },
  paymentterm: {
    t: 'เงื่อนไขชำระ (Payment Terms)',
    s: 'พรีเซ็ตเงื่อนไขชำระเงิน — กำหนด Net days + ส่วนลดจ่ายเร็ว (เช่น 2/10 N30) ใช้กับใบสำคัญจ่าย AP',
  },
  bank: {
    t: 'ธนาคารและบัญชีธนาคาร',
    s: 'รายการบัญชีธนาคารของบริษัท — ใช้กระทบยอด, จ่ายเงิน, รับเงิน',
  },
  asset: {
    t: 'ทรัพย์สิน Master',
    s: 'อาคาร, ที่ดิน, ป้าย — Master ใช้กับ Fixed Asset และ Property Tax',
  },
  loan: {
    t: 'เงินกู้บริษัท',
    s: 'เงินกู้ระยะยาว/ระยะสั้น — ใช้คำนวณ Capitalization Rate (TFRS 23)',
  },
  doc: {
    t: 'เลขเอกสาร (Document Sequence)',
    s: 'ตั้งค่าเลขรันใบสำคัญทุกประเภท + รูปแบบ Prefix + การ Reset',
  },
  period: {
    t: 'ปีบัญชี & งวด (Accounting Periods)',
    s: 'งวดบัญชีรายเดือน + สถานะเปิด/ปิด — ใช้ Lock JV ย้อนหลัง',
  },
  currency: {
    t: 'สกุลเงิน & FX Rate',
    s: 'สกุลเงินที่ใช้ + อัตราแลกเปลี่ยน — Update อัตโนมัติทุกวันทำการ',
  },
};

const SEARCH_BG =
  "url(\"data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='14' height='14' viewBox='0 0 24 24' fill='none' stroke='%238a8a93' stroke-width='2'%3E%3Ccircle cx='11' cy='11' r='7'/%3E%3Cpath d='M21 21l-4.3-4.3'/%3E%3C/svg%3E\")";

function fmtSeq(n: number): string {
  return String(n).padStart(6, '0');
}

// Sample voucher-number renderer: needs prefix + format/reset from the DocumentType
// and the next-up sequence number. `next` is computed from the live counter
// (rangeStart + totalUsed) so the preview matches the next JV that will be emitted.
function bookSample(d: DocumentType, b: VoucherBook, next: number): string {
  const yy = '26';
  const mm = '05';
  const yyyy = '2026';
  const pref = d.prefix;
  const previewN = fmtSeq(next);
  if (d.format === 'MONTHLY') return `${pref}-${yyyy}-${mm}-${previewN}`;
  if (d.format === 'YEARLY_RUNNING') return `${pref}-${yyyy}-${previewN}`;
  if (d.format === 'LIFETIME') return `${pref}-${previewN}`;
  return `${pref}-${yy}${mm}-${previewN}`;
}

// Thai reset-rule label — schema uses enum, prototype showed Thai strings.
function resetLabel(r: DocumentType['resetRule']): string {
  return r === 'MONTHLY' ? 'รายเดือน' : r === 'YEARLY' ? 'รายปี' : 'ไม่ Reset';
}

// Asset-master enum → Thai label (prototype shows อาคาร/ป้าย/etc.).
function assetTypeLabel(t: AssetMasterType): string {
  switch (t) {
    case 'BUILDING':
      return 'อาคาร';
    case 'SIGNAGE':
      return 'ป้าย';
    case 'LAND':
      return 'ที่ดิน';
    case 'MACHINE':
      return 'เครื่องจักร';
    case 'VEHICLE':
      return 'ยานพาหนะ';
    default:
      return 'อื่นๆ';
  }
}

// Loan-purpose enum → display label.
function loanPurposeLabel(p: LoanPurpose): string {
  return p === 'SPECIFIC' ? 'Specific' : 'General';
}

// Bank-account type enum → Thai label.
function bankAccountTypeLabel(t: BankAccountType): string {
  switch (t) {
    case 'CURRENT':
      return 'กระแสรายวัน';
    case 'SAVINGS':
      return 'ออมทรัพย์';
    case 'FIXED':
      return 'ฝากประจำ';
    case 'FOREIGN':
      return 'เงินตราต่างประเทศ';
  }
}

// '202604' → '04/2026' for table display.
function periodCodeToDisplay(code: string): string {
  if (code.length !== 6) return code;
  return `${code.slice(4, 6)}/${code.slice(0, 4)}`;
}

function statusBadge(active: boolean) {
  return active ? (
    <span className="badge positive" style={{ fontSize: 13 }}>
      <span className="bdot" />
      ใช้งาน
    </span>
  ) : (
    <span className="badge neutral" style={{ fontSize: 13 }}>
      ปิด
    </span>
  );
}

// Row-level edit / soft-delete cell — shared by the 9 generic masters and the
// 4 special ones (DocumentType / Currency use a TODO-alert for edit because
// their schemas need richer modals; COA / Company are read-only at the API).
interface RowActionsProps {
  rowLabel: string;
  onEdit: () => void;
  onDelete?: () => void;
  deleting?: boolean;
}

function RowActions({ rowLabel, onEdit, onDelete, deleting }: RowActionsProps) {
  return (
    <td>
      <div style={{ display: 'flex', gap: 4 }}>
        <button
          className="btn sm ghost"
          style={{ height: 26, fontSize: 13 }}
          title="แก้ไข"
          onClick={onEdit}
        >
          <Icon name="settings" size={14} />
        </button>
        {onDelete && (
          <button
            className="btn sm ghost"
            style={{ height: 26, fontSize: 13, color: 'var(--negative)' }}
            title="ปิดใช้งาน"
            disabled={deleting}
            onClick={() => {
              if (confirm('ยืนยันปิดใช้งาน ' + rowLabel + '?')) onDelete();
            }}
          >
            <Icon name="x" size={14} />
          </button>
        )}
      </div>
    </td>
  );
}

// Standard placeholder rows kept inside the table body — preserves the prototype DOM.
function LoadingRow({ colSpan }: { colSpan: number }) {
  return (
    <tr>
      <td colSpan={colSpan} className="muted" style={{ padding: 24, textAlign: 'center' }}>
        กำลังโหลด…
      </td>
    </tr>
  );
}

function ErrorRow({ colSpan, message }: { colSpan: number; message: string }) {
  return (
    <tr>
      <td
        colSpan={colSpan}
        className="muted"
        style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
      >
        ไม่สามารถโหลดข้อมูล: {message}
      </td>
    </tr>
  );
}

export const Route = createFileRoute('/master')({
  component: MasterDataPage,
});

export function MasterDataPage() {
  const [sel, setSel] = useState<MasterId>('coa');
  const [showAdd, setShowAdd] = useState(false);

  const allItems = MD_NAV.flatMap((g) => g.items);
  const current = allItems.find((i) => i.id === sel);
  const currentLabel = current?.label ?? '';

  return (
    <div className="page" style={{ paddingRight: 0, paddingBottom: 0 }}>
      <PageHead
        crumbs={['ระบบ', 'Master Data']}
        title="Master Data — จัดการข้อมูลตั้งต้นของระบบ"
        actions={
          <>
            <HelpButton guide="master" />
            <button className="btn">
              <Icon name="upload" size={14} /> Import จาก Excel
            </button>
            <button className="btn">
              <Icon name="download" size={14} /> Export
            </button>
            {/* The payment-term master is permission-gated (paymentterm.manage);
                the rest of the masters keep their always-visible add button. */}
            {sel === 'paymentterm' ? (
              <Can permission="paymentterm.manage">
                <button className="btn primary" onClick={() => setShowAdd(true)}>
                  <Icon name="plus" size={14} /> เพิ่ม {currentLabel}
                </button>
              </Can>
            ) : (
              <button className="btn primary" onClick={() => setShowAdd(true)}>
                <Icon name="plus" size={14} /> เพิ่ม {currentLabel}
              </button>
            )}
          </>
        }
      />

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '260px 1fr',
          gap: 0,
          marginRight: 24,
          marginBottom: 24,
          minHeight: 600,
        }}
      >
        <div
          style={{
            background: '#fafafa',
            border: '1px solid var(--border)',
            borderRadius: '8px 0 0 8px',
            padding: '10px 0',
            overflow: 'auto',
          }}
        >
          {MD_NAV.map((group) => (
            <div key={group.section}>
              <div
                style={{
                  padding: '14px 16px 6px',
                  fontSize: 14,
                  letterSpacing: 0.4,
                  textTransform: 'uppercase',
                  color: 'var(--text-faint)',
                  fontWeight: 500,
                }}
              >
                {group.section}
              </div>
              {group.items.map((item) => {
                const active = sel === item.id;
                return (
                  <div
                    key={item.id}
                    onClick={() => setSel(item.id)}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 10,
                      padding: '10px 14px',
                      margin: '1px 6px',
                      fontSize: 14,
                      borderRadius: 6,
                      cursor: 'pointer',
                      background: active ? 'var(--brand-tint)' : 'transparent',
                      color: active ? 'var(--brand)' : '#3f3f46',
                      fontWeight: active ? 600 : 400,
                      position: 'relative',
                    }}
                  >
                    <Icon name={item.icon} size={16} />
                    <span style={{ flex: 1 }}>{item.label}</span>
                  </div>
                );
              })}
            </div>
          ))}
        </div>

        <div
          style={{
            background: 'white',
            border: '1px solid var(--border)',
            borderLeft: 'none',
            borderRadius: '0 8px 8px 0',
            padding: '18px 22px',
            overflow: 'auto',
          }}
        >
          <MDContent sel={sel} />
        </div>
      </div>

      {showAdd &&
        (sel === 'branch' ? (
          <BranchFormModal mode="add" onClose={() => setShowAdd(false)} />
        ) : sel === 'dept' ? (
          <DepartmentFormModal mode="add" onClose={() => setShowAdd(false)} />
        ) : sel === 'loan' ? (
          <LoanFormModal mode="add" onClose={() => setShowAdd(false)} />
        ) : sel === 'paymentterm' ? (
          <PaymentTermFormModal mode="add" onClose={() => setShowAdd(false)} />
        ) : sel === 'bank' ? (
          <BankFormModal mode="add" onClose={() => setShowAdd(false)} />
        ) : (
          <AddMasterModal
            name={currentLabel}
            sel={sel}
            mode="add"
            onClose={() => setShowAdd(false)}
          />
        ))}
    </div>
  );
}

function MDContent({ sel }: { sel: MasterId }) {
  const [q, setQ] = useState('');
  const [showActivity, setShowActivity] = useState(false);
  const title = TITLES[sel];

  return (
    <>
      <div style={{ marginBottom: 16 }}>
        <h3 style={{ margin: 0, fontSize: 20, fontWeight: 600, fontFamily: 'var(--font-display)' }}>
          {title.t}
        </h3>
        <div className="meta" style={{ marginTop: 4 }}>
          {title.s}
        </div>
      </div>

      <div style={{ display: 'flex', gap: 10, marginBottom: 14 }}>
        <input
          className="input search w-lg"
          placeholder="ค้นหา..."
          value={q}
          onChange={(e) => setQ(e.target.value)}
          style={{
            paddingLeft: 32,
            backgroundImage: SEARCH_BG,
            backgroundRepeat: 'no-repeat',
            backgroundPosition: '10px center',
          }}
        />
        <select className="select" defaultValue="active">
          <option value="active">สถานะ: ใช้งาน</option>
          <option value="inactive">ปิดใช้งาน</option>
          <option value="all">ทั้งหมด</option>
        </select>
        <select className="select" defaultValue="code">
          <option value="code">เรียงตาม: รหัส</option>
          <option value="name">ชื่อ</option>
          <option value="created">วันสร้าง</option>
        </select>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          <button
            type="button"
            className="btn ghost"
            onClick={() => setShowActivity(true)}
            style={{ fontSize: 13, padding: '4px 10px' }}
          >
            Activity Log: ดูประวัติ
          </button>
        </div>
      </div>

      {sel === 'coa' && <COAList q={q} />}
      {sel === 'company' && <CompanyList />}
      {sel === 'branch' && <BranchList q={q} />}
      {sel === 'dept' && <DeptList q={q} />}
      {sel === 'project' && <ProjectList q={q} />}
      {sel === 'employee' && <EmployeeList q={q} />}
      {sel === 'vendor' && <VendorList q={q} />}
      {sel === 'customer' && <CustomerList q={q} />}
      {sel === 'paymentterm' && <PaymentTermList q={q} />}
      {sel === 'bank' && <BankList />}
      {sel === 'asset' && <AssetMasterList />}
      {sel === 'loan' && <LoanList />}
      {sel === 'doc' && <DocSeqList />}
      {sel === 'period' && <PeriodList />}
      {sel === 'currency' && <CurrencyList />}
      {showActivity && <ActivityLogModal onClose={() => setShowActivity(false)} />}
    </>
  );
}

function ActivityLogModal({ onClose }: { onClose: () => void }) {
  return (
    <div style={editModalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={editModalBox}>
        <div style={editModalHeader}>
          <h3 style={editModalTitle}>ประวัติการแก้ไข (Activity Log)</h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div
          style={{
            ...editModalBody,
            alignItems: 'center',
            textAlign: 'center',
            padding: '40px 22px',
          }}
        >
          <div style={{ fontSize: 15, fontWeight: 600 }}>ยังไม่มีการบันทึกประวัติการแก้ไข</div>
          <div className="meta" style={{ maxWidth: 380, lineHeight: 1.6 }}>
            ระบบ Audit Log จะบันทึกการเพิ่ม แก้ไข
            และลบข้อมูลหลักโดยอัตโนมัติเมื่อเปิดใช้งานฟีเจอร์นี้ (ยังไม่ได้ติดตั้งในเฟสนี้)
          </div>
        </div>
        <div style={editModalFooter}>
          <button className="btn primary" onClick={onClose}>
            ปิด
          </button>
        </div>
      </div>
    </div>
  );
}

function COAList({ q }: { q: string }) {
  const companyId = useActiveCompanyId();
  const { data: rowsRaw = [], isPending, error } = useCoa(companyId);
  const rows: ChartOfAccount[] = rowsRaw.filter(
    (r) => !q || r.code.includes(q) || r.nameTh.toLowerCase().includes(q.toLowerCase()),
  );
  const COLS = 9;
  return (
    <div className="tbl-shell">
      <div className="tbl-wrap" style={{ maxHeight: 500 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>รหัส</th>
              <th>ชื่อบัญชี</th>
              <th>ประเภท</th>
              <th>ระดับ</th>
              <th>ลักษณะ</th>
              <th>สกุล</th>
              <th className="num">ยอดคงเหลือ</th>
              <th>Used in JV</th>
              <th>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {isPending && !rowsRaw.length ? (
              <LoadingRow colSpan={COLS} />
            ) : error ? (
              <ErrorRow colSpan={COLS} message={error.message} />
            ) : (
              rows.map((r) => (
                <tr key={r.code}>
                  <td className="code">
                    <b>{r.code}</b>
                  </td>
                  {/* COA levels in DB are 1..6; prototype indented by level so we keep the same idea. */}
                  <td style={{ paddingLeft: Math.max(0, r.level - 1) * 12 }}>{r.nameTh}</td>
                  <td className="muted">{r.type}</td>
                  <td className="muted">
                    {r.level === 1 ? 'หัวบัญชี' : r.isControl ? `ระดับ ${r.level}` : 'บัญชีย่อย'}
                  </td>
                  <td className="muted">
                    {r.type === 'ASSET' || r.type === 'EXPENSE' || r.type === 'COST'
                      ? 'เดบิต'
                      : 'เครดิต'}
                  </td>
                  <td className="muted">THB</td>
                  {/* Balance + usedInJv are not yet exposed by GET /coa — render as '—'. */}
                  <td className="num tnum muted">—</td>
                  <td className="muted">—</td>
                  {/* COA is read-only at the API (no PATCH/DELETE) — no row actions. */}
                  <td className="muted">—</td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function CompanyList() {
  const { data: rows = [], isPending, error } = useCompanies();
  const COLS = 7;
  return (
    <div className="tbl-shell">
      <table className="tbl">
        <thead>
          <tr>
            <th>รหัส</th>
            <th>ชื่อภาษาไทย</th>
            <th>ชื่อภาษาอังกฤษ</th>
            <th>เลขผู้เสียภาษี</th>
            <th>ธีมสี</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {isPending && !rows.length ? (
            <LoadingRow colSpan={COLS} />
          ) : error ? (
            <ErrorRow colSpan={COLS} message={error.message} />
          ) : (
            rows.map((c: Company) => (
              <tr key={c.code}>
                <td className="code">
                  <b>{c.code}</b>
                </td>
                <td>{c.nameTh}</td>
                <td>{c.nameEn}</td>
                <td className="code muted">{c.taxId}</td>
                <td>
                  <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ width: 24, height: 14, borderRadius: 3, background: c.color }} />
                    <span className="code">{c.color}</span>
                  </span>
                </td>
                <td>{statusBadge(true)}</td>
                {/* Company is read-only at the API (no PATCH/DELETE) — no row actions. */}
                <td className="muted">—</td>
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

function BranchList({ q }: { q: string }) {
  const companyId = useActiveCompanyId();
  const { data: rowsRaw = [], isPending, error } = useBranches(companyId);
  const deleteBranch = useDeleteBranch(companyId);
  const [editingRow, setEditingRow] = useState<Branch | null>(null);
  const rows: Branch[] = rowsRaw.filter(
    (r) =>
      !q ||
      r.code.toLowerCase().includes(q.toLowerCase()) ||
      r.nameTh.toLowerCase().includes(q.toLowerCase()),
  );
  const COLS = 6;
  return (
    <div className="tbl-shell">
      <div className="tbl-wrap" style={{ maxHeight: 500 }}>
        <table className="tbl">
          <thead>
            <tr>
              <th>รหัส</th>
              <th>ชื่อสาขา</th>
              <th>ลำดับที่ของสาขา</th>
              <th>ที่อยู่</th>
              <th>สถานะ</th>
              <th>การกระทำ</th>
            </tr>
          </thead>
          <tbody>
            {isPending && !rowsRaw.length ? (
              <LoadingRow colSpan={COLS} />
            ) : error ? (
              <ErrorRow colSpan={COLS} message={error.message} />
            ) : (
              rows.map((b) => (
                <tr key={b.id}>
                  <td className="code">
                    <b>{b.code}</b>
                  </td>
                  <td>
                    <b>{b.nameTh}</b>
                  </td>
                  <td className="code muted">{b.taxBranch ?? '—'}</td>
                  <td className="muted">{b.address ?? '—'}</td>
                  <td>{statusBadge(b.isActive)}</td>
                  <RowActions
                    rowLabel={b.code}
                    onEdit={() => setEditingRow(b)}
                    onDelete={() => {
                      void deleteBranch.mutateAsync(b.id);
                    }}
                    deleting={deleteBranch.isPending}
                  />
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>
      {editingRow && (
        <BranchFormModal mode="edit" initial={editingRow} onClose={() => setEditingRow(null)} />
      )}
    </div>
  );
}

function DeptList({ q }: { q: string }) {
  const companyId = useActiveCompanyId();
  const { data: rowsRaw = [], isPending, error } = useDepartments(companyId);
  const deleteDept = useDeleteDepartment(companyId);
  const { map: branchMap } = useBranchNameMap(companyId);
  const [editingRow, setEditingRow] = useState<Department | null>(null);
  const rows: Department[] = rowsRaw.filter(
    (r) =>
      !q ||
      r.code.toLowerCase().includes(q.toLowerCase()) ||
      r.nameTh.toLowerCase().includes(q.toLowerCase()),
  );
  const COLS = 8;
  return (
    <div className="tbl-shell">
      <table className="tbl">
        <thead>
          <tr>
            <th>รหัส</th>
            <th>ชื่อแผนก</th>
            <th>รหัสย่อ</th>
            <th>สาขา</th>
            <th>หัวหน้า</th>
            <th className="num">จำนวนพนักงาน</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {isPending && !rowsRaw.length ? (
            <LoadingRow colSpan={COLS} />
          ) : error ? (
            <ErrorRow colSpan={COLS} message={error.message} />
          ) : (
            rows.map((d) => (
              <tr key={d.id}>
                <td className="code">
                  <b>{d.code}</b>
                </td>
                <td>
                  <b>{d.nameTh}</b>
                </td>
                <td className="code muted">{d.shortCode}</td>
                <td className="muted">{resolveName(branchMap, d.branchId)}</td>
                <td className="muted">{d.head ?? '—'}</td>
                <td className="num">{d.headcount}</td>
                <td>{statusBadge(d.isActive)}</td>
                <RowActions
                  rowLabel={d.code}
                  onEdit={() => setEditingRow(d)}
                  onDelete={() => {
                    void deleteDept.mutateAsync(d.id);
                  }}
                  deleting={deleteDept.isPending}
                />
              </tr>
            ))
          )}
        </tbody>
      </table>
      {editingRow && (
        <DepartmentFormModal mode="edit" initial={editingRow} onClose={() => setEditingRow(null)} />
      )}
    </div>
  );
}

function ProjectList({ q }: { q: string }) {
  const companyId = useActiveCompanyId();
  const { data: rowsRaw = [], isPending, error } = useProjects(companyId);
  const deleteProject = useDeleteProject(companyId);
  const { map: branchMap } = useBranchNameMap(companyId);
  const [editingRow, setEditingRow] = useState<Project | null>(null);
  const rows: Project[] = rowsRaw.filter(
    (r) =>
      !q ||
      r.code.toLowerCase().includes(q.toLowerCase()) ||
      r.nameTh.toLowerCase().includes(q.toLowerCase()),
  );
  const COLS = 8;
  return (
    <div className="tbl-shell">
      <table className="tbl">
        <thead>
          <tr>
            <th>รหัส</th>
            <th>ชื่อโครงการ</th>
            <th>ประเภท</th>
            <th>สาขา / หน่วยงาน</th>
            <th>PM</th>
            <th className="num">งบประมาณ</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {isPending && !rowsRaw.length ? (
            <LoadingRow colSpan={COLS} />
          ) : error ? (
            <ErrorRow colSpan={COLS} message={error.message} />
          ) : (
            rows.map((p) => (
              <tr key={p.id}>
                <td className="code">
                  <b>{p.code}</b>
                </td>
                <td>{p.nameTh}</td>
                <td>
                  {p.type === 'CIP' ? (
                    <span className="badge brand">CIP</span>
                  ) : p.type === 'SALES' ? (
                    <span className="badge info">Sales</span>
                  ) : (
                    <span className="badge neutral">Cost Center</span>
                  )}
                </td>
                {/* branchId is a UUID — resolved against the branches list to its Thai name. */}
                <td className="muted">{resolveName(branchMap, p.branchId)}</td>
                <td className="muted">{p.pm ?? '—'}</td>
                <td className="num tnum">{fmt(toNumber(p.budget))}</td>
                <td>{statusBadge(p.isActive)}</td>
                <RowActions
                  rowLabel={p.code}
                  onEdit={() => setEditingRow(p)}
                  onDelete={() => {
                    void deleteProject.mutateAsync(p.id);
                  }}
                  deleting={deleteProject.isPending}
                />
              </tr>
            ))
          )}
        </tbody>
      </table>
      {editingRow && (
        <AddMasterModal
          name="Project / Cost Center"
          sel="project"
          mode="edit"
          initial={editingRow}
          onClose={() => setEditingRow(null)}
        />
      )}
    </div>
  );
}

function EmployeeList({ q }: { q: string }) {
  const companyId = useActiveCompanyId();
  const { data: rowsRaw = [], isPending, error } = useEmployees(companyId);
  const deleteEmployee = useDeleteEmployee(companyId);
  const { map: deptMap } = useDepartmentNameMap(companyId);
  const [editingRow, setEditingRow] = useState<Employee | null>(null);
  const rows: Employee[] = rowsRaw.filter(
    (r) =>
      !q ||
      r.code.toLowerCase().includes(q.toLowerCase()) ||
      r.nameTh.toLowerCase().includes(q.toLowerCase()),
  );
  const COLS = 9;
  return (
    <div className="tbl-shell">
      <table className="tbl">
        <thead>
          <tr>
            <th>รหัส</th>
            <th>ชื่อ-สกุล</th>
            <th>แผนก</th>
            <th>ตำแหน่ง</th>
            <th>Email</th>
            <th>วันเข้าทำงาน</th>
            <th>บัญชีธนาคาร</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {isPending && !rowsRaw.length ? (
            <LoadingRow colSpan={COLS} />
          ) : error ? (
            <ErrorRow colSpan={COLS} message={error.message} />
          ) : (
            rows.map((e) => (
              <tr key={e.id}>
                <td className="code">
                  <b>{e.code}</b>
                </td>
                <td>
                  <b>{e.nameTh}</b>
                </td>
                {/* departmentId is a UUID — resolved against the departments list to its Thai name. */}
                <td className="muted">{resolveName(deptMap, e.departmentId)}</td>
                <td className="muted">{e.position}</td>
                <td className="muted">{e.email ?? '—'}</td>
                <td>{fmtDate(e.joined)}</td>
                <td className="code muted">{e.bank ?? '—'}</td>
                <td>{statusBadge(e.isActive)}</td>
                <RowActions
                  rowLabel={e.code}
                  onEdit={() => setEditingRow(e)}
                  onDelete={() => {
                    void deleteEmployee.mutateAsync(e.id);
                  }}
                  deleting={deleteEmployee.isPending}
                />
              </tr>
            ))
          )}
        </tbody>
      </table>
      {editingRow && (
        <AddMasterModal
          name="พนักงาน"
          sel="employee"
          mode="edit"
          initial={editingRow}
          onClose={() => setEditingRow(null)}
        />
      )}
    </div>
  );
}

function VendorList({ q }: { q: string }) {
  const companyId = useActiveCompanyId();
  const { data: rowsRaw = [], isPending, error } = useVendors(companyId);
  const deleteVendor = useDeleteVendor(companyId);
  const [editingRow, setEditingRow] = useState<Vendor | null>(null);
  const rows: Vendor[] = rowsRaw.filter(
    (r) =>
      !q ||
      r.code.toLowerCase().includes(q.toLowerCase()) ||
      r.nameTh.toLowerCase().includes(q.toLowerCase()),
  );
  const COLS = 9;
  return (
    <div className="tbl-shell">
      <table className="tbl">
        <thead>
          <tr>
            <th>รหัส</th>
            <th>ชื่อผู้ขาย</th>
            <th>เลขผู้เสียภาษี</th>
            <th>ประเทศ</th>
            <th>Payment Terms</th>
            <th>Email</th>
            <th>สกุลเงิน</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {isPending && !rowsRaw.length ? (
            <LoadingRow colSpan={COLS} />
          ) : error ? (
            <ErrorRow colSpan={COLS} message={error.message} />
          ) : (
            rows.map((v) => (
              <tr key={v.id}>
                <td className="code">
                  <b>{v.code}</b>
                </td>
                <td>
                  <b>{v.nameTh}</b>
                </td>
                <td className="code muted">{v.taxId ?? '—'}</td>
                <td className="muted">{v.country}</td>
                <td className="muted">{v.terms ?? '—'}</td>
                <td className="muted">{v.email ?? '—'}</td>
                <td>
                  {v.currency !== 'THB' ? (
                    <span className="badge warning">{v.currency}</span>
                  ) : (
                    <span className="muted">{v.currency}</span>
                  )}
                </td>
                <td>{statusBadge(v.isActive)}</td>
                <RowActions
                  rowLabel={v.code}
                  onEdit={() => setEditingRow(v)}
                  onDelete={() => {
                    void deleteVendor.mutateAsync(v.id);
                  }}
                  deleting={deleteVendor.isPending}
                />
              </tr>
            ))
          )}
        </tbody>
      </table>
      {editingRow && (
        <AddMasterModal
          name="ผู้ขาย"
          sel="vendor"
          mode="edit"
          initial={editingRow}
          onClose={() => setEditingRow(null)}
        />
      )}
    </div>
  );
}

function CustomerList({ q }: { q: string }) {
  const companyId = useActiveCompanyId();
  const { data: rowsRaw = [], isPending, error } = useCustomers(companyId);
  const deleteCustomer = useDeleteCustomer(companyId);
  const [editingRow, setEditingRow] = useState<Customer | null>(null);
  const rows: Customer[] = rowsRaw.filter(
    (r) =>
      !q ||
      r.code.toLowerCase().includes(q.toLowerCase()) ||
      r.nameTh.toLowerCase().includes(q.toLowerCase()),
  );
  const COLS = 9;
  return (
    <div className="tbl-shell">
      <table className="tbl">
        <thead>
          <tr>
            <th>รหัส</th>
            <th>ชื่อลูกค้า</th>
            <th>เลขผู้เสียภาษี</th>
            <th>Payment Terms</th>
            <th className="num">Credit Limit</th>
            <th className="num">ยอดขาย YTD</th>
            <th className="num">การใช้วงเงิน</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {isPending && !rowsRaw.length ? (
            <LoadingRow colSpan={COLS} />
          ) : error ? (
            <ErrorRow colSpan={COLS} message={error.message} />
          ) : (
            rows.map((c) => {
              const credit = toNumber(c.creditLimit);
              const ytd = toNumber(c.ytdSales);
              const pct = credit > 0 ? Math.round(((ytd / credit) * 100 * 5) / 12) : 0;
              return (
                <tr key={c.id}>
                  <td className="code">
                    <b>{c.code}</b>
                  </td>
                  <td>
                    <b>{c.nameTh}</b>
                  </td>
                  <td className="code muted">{c.taxId ?? '—'}</td>
                  <td className="muted">{c.terms ?? '—'}</td>
                  <td className="num tnum">{fmt(credit)}</td>
                  <td className="num tnum">{fmt(ytd)}</td>
                  <td className="num">
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                      <div
                        style={{
                          flex: 1,
                          height: 6,
                          borderRadius: 3,
                          background: '#f0f0f3',
                          overflow: 'hidden',
                        }}
                      >
                        <div
                          style={{
                            width: `${Math.min(100, pct)}%`,
                            height: '100%',
                            background:
                              pct > 80
                                ? 'var(--negative)'
                                : pct > 60
                                  ? 'var(--warning)'
                                  : 'var(--positive)',
                          }}
                        />
                      </div>
                      <span className="meta" style={{ minWidth: 36, textAlign: 'right' }}>
                        {pct}%
                      </span>
                    </div>
                  </td>
                  <td>{statusBadge(c.isActive)}</td>
                  <RowActions
                    rowLabel={c.code}
                    onEdit={() => setEditingRow(c)}
                    onDelete={() => {
                      void deleteCustomer.mutateAsync(c.id);
                    }}
                    deleting={deleteCustomer.isPending}
                  />
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {editingRow && (
        <AddMasterModal
          name="ลูกค้า"
          sel="customer"
          mode="edit"
          initial={editingRow}
          onClose={() => setEditingRow(null)}
        />
      )}
    </div>
  );
}

// AP Phase C1 — Payment-Terms master. Per-company presets (code, label, netDays,
// discountPercent, discountDays, isActive). All mutating actions are gated by
// `paymentterm.manage` (the server also enforces it via @RequirePermission).
function PaymentTermList({ q }: { q: string }) {
  const companyId = useActiveCompanyId();
  // activeOnly=false so the admin table also shows deactivated presets.
  const { data: rowsRaw = [], isPending, error } = usePaymentTerms(companyId, false);
  const deactivate = useDeactivatePaymentTerm(companyId);
  const [editingRow, setEditingRow] = useState<PaymentTerm | null>(null);
  const rows: PaymentTerm[] = rowsRaw.filter(
    (r) =>
      !q ||
      r.code.toLowerCase().includes(q.toLowerCase()) ||
      r.label.toLowerCase().includes(q.toLowerCase()),
  );
  const COLS = 6;
  return (
    <div className="tbl-shell">
      <table className="tbl">
        <thead>
          <tr>
            <th>รหัส</th>
            <th>ชื่อ</th>
            <th className="num">Net (วัน)</th>
            <th>ส่วนลดจ่ายเร็ว</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {isPending && !rowsRaw.length ? (
            <LoadingRow colSpan={COLS} />
          ) : error ? (
            <ErrorRow colSpan={COLS} message={error.message} />
          ) : rows.length === 0 ? (
            <tr>
              <td colSpan={COLS} className="muted" style={{ padding: 24, textAlign: 'center' }}>
                ยังไม่มีเงื่อนไขชำระ — กด “เพิ่ม” เพื่อสร้างพรีเซ็ตแรก
              </td>
            </tr>
          ) : (
            rows.map((t) => {
              const pct = toNumber(t.discountPercent);
              return (
                <tr key={t.id}>
                  <td className="code">
                    <b>{t.code}</b>
                  </td>
                  <td>
                    <b>{t.label}</b>
                  </td>
                  <td className="num tnum">{t.netDays}</td>
                  <td className="muted">{pct > 0 ? `${pct}% ภายใน ${t.discountDays} วัน` : '—'}</td>
                  <td>{statusBadge(t.isActive)}</td>
                  <Can permission="paymentterm.manage">
                    <RowActions
                      rowLabel={t.code}
                      onEdit={() => setEditingRow(t)}
                      onDelete={() => {
                        void deactivate.mutateAsync(t.id);
                      }}
                      deleting={deactivate.isPending}
                    />
                  </Can>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
      {editingRow && (
        <PaymentTermFormModal
          mode="edit"
          initial={editingRow}
          onClose={() => setEditingRow(null)}
        />
      )}
    </div>
  );
}

interface PaymentTermFormModalProps {
  mode: 'add' | 'edit';
  initial?: PaymentTerm;
  onClose: () => void;
}

function PaymentTermFormModal({ mode, initial, onClose }: PaymentTermFormModalProps) {
  const companyId = useActiveCompanyId();
  const createTerm = useCreatePaymentTerm(companyId);
  const updateTerm = useUpdatePaymentTerm(companyId);

  const [code, setCode] = useState(initial?.code ?? '');
  const [label, setLabel] = useState(initial?.label ?? '');
  const [netDays, setNetDays] = useState(initial ? String(initial.netDays) : '30');
  const [discountPercent, setDiscountPercent] = useState(
    initial ? String(toNumber(initial.discountPercent)) : '0',
  );
  const [discountDays, setDiscountDays] = useState(initial ? String(initial.discountDays) : '0');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const submitting = createTerm.isPending || updateTerm.isPending;
  const netNum = Number(netDays);
  const pctNum = Number(discountPercent);
  const discDaysNum = Number(discountDays);
  const disabled =
    !code.trim() ||
    !label.trim() ||
    !Number.isFinite(netNum) ||
    netNum < 0 ||
    pctNum < 0 ||
    pctNum > 100 ||
    discDaysNum < 0 ||
    submitting;

  async function handleSubmit() {
    setError(null);
    try {
      if (mode === 'edit') {
        if (!initial) {
          setError('ไม่พบเงื่อนไขชำระ');
          return;
        }
        // code is the immutable per-company natural key — not sent on update.
        const data: UpdatePaymentTerm = {
          label: label.trim(),
          netDays: netNum,
          discountPercent: pctNum,
          discountDays: discDaysNum,
          isActive,
        };
        await updateTerm.mutateAsync({ id: initial.id, data });
      } else {
        const input: CreatePaymentTerm = {
          code: code.trim(),
          label: label.trim(),
          netDays: netNum,
          discountPercent: pctNum,
          discountDays: discDaysNum,
          isActive,
        };
        await createTerm.mutateAsync(input);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <div style={editModalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={editModalBox}>
        <div style={editModalHeader}>
          <h3 style={editModalTitle}>
            {mode === 'edit' ? 'แก้ไขเงื่อนไขชำระ' : 'เพิ่มเงื่อนไขชำระ'}
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={editModalBody}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>รหัส</label>
              <input
                className="input code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={mode === 'edit'}
                placeholder="เช่น 2/10N30"
                aria-label="รหัสเงื่อนไขชำระ"
              />
            </div>
            <div>
              <label style={editModalLabel}>ชื่อ</label>
              <input
                className="input"
                value={label}
                onChange={(e) => setLabel(e.target.value)}
                placeholder="เช่น 2% ภายใน 10 วัน, สุทธิ 30 วัน"
                aria-label="ชื่อเงื่อนไขชำระ"
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>Net (วัน)</label>
              <input
                type="number"
                className="input tnum"
                value={netDays}
                onChange={(e) => setNetDays(e.target.value)}
                min={0}
                step={1}
                style={{ textAlign: 'right' }}
                aria-label="Net days"
              />
            </div>
            <div>
              <label style={editModalLabel}>ส่วนลด (%)</label>
              <input
                type="number"
                className="input tnum"
                value={discountPercent}
                onChange={(e) => setDiscountPercent(e.target.value)}
                min={0}
                max={100}
                step={0.01}
                style={{ textAlign: 'right' }}
                aria-label="ส่วนลด %"
              />
            </div>
            <div>
              <label style={editModalLabel}>ภายใน (วัน)</label>
              <input
                type="number"
                className="input tnum"
                value={discountDays}
                onChange={(e) => setDiscountDays(e.target.value)}
                min={0}
                step={1}
                style={{ textAlign: 'right' }}
                aria-label="ส่วนลดภายในกี่วัน"
              />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            ใช้งาน
          </label>
          {error && <div style={editModalError}>บันทึกไม่สำเร็จ: {error}</div>}
        </div>
        <div style={editModalFooter}>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={disabled}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  );
}

function BankList() {
  const companyId = useActiveCompanyId();
  const { data: rows = [], isPending, error } = useBankAccounts(companyId);
  const deleteBank = useDeleteBankAccount(companyId);
  const [editingRow, setEditingRow] = useState<BankAccount | null>(null);
  const COLS = 9;
  return (
    <div className="tbl-shell">
      <table className="tbl">
        <thead>
          <tr>
            <th>รหัส</th>
            <th>ธนาคาร</th>
            <th>เลขที่บัญชี</th>
            <th>ชื่อบัญชี</th>
            <th>ประเภท</th>
            <th>สาขา</th>
            <th className="num">ยอดคงเหลือ</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {isPending && !rows.length ? (
            <LoadingRow colSpan={COLS} />
          ) : error ? (
            <ErrorRow colSpan={COLS} message={error.message} />
          ) : (
            rows.map((b: BankAccount) => (
              <tr key={b.id}>
                <td className="code">
                  <b>{b.code}</b>
                </td>
                <td>
                  <b>{b.bank}</b>
                </td>
                <td className="code">{b.accNo}</td>
                <td className="muted">{b.nameTh}</td>
                <td className="muted">{bankAccountTypeLabel(b.type)}</td>
                <td className="muted">{b.branch ?? '—'}</td>
                <td className="num tnum">{fmt(toNumber(b.balance))}</td>
                <td>{statusBadge(b.isActive)}</td>
                <RowActions
                  rowLabel={b.code}
                  onEdit={() => setEditingRow(b)}
                  onDelete={() => {
                    void deleteBank.mutateAsync(b.id);
                  }}
                  deleting={deleteBank.isPending}
                />
              </tr>
            ))
          )}
        </tbody>
      </table>
      {editingRow && (
        <BankFormModal mode="edit" initial={editingRow} onClose={() => setEditingRow(null)} />
      )}
    </div>
  );
}

function AssetMasterList() {
  const companyId = useActiveCompanyId();
  const { data: rows = [], isPending, error } = useAssetMasters(companyId);
  const deleteAsset = useDeleteAssetMaster(companyId);
  const [editingRow, setEditingRow] = useState<AssetMaster | null>(null);
  const COLS = 9;
  return (
    <div className="tbl-shell">
      <table className="tbl">
        <thead>
          <tr>
            <th>รหัส</th>
            <th>ประเภท</th>
            <th>ชื่อ</th>
            <th>ที่ตั้ง</th>
            <th>ขนาด</th>
            <th className="num">มูลค่า</th>
            <th>หน่วยงาน</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {isPending && !rows.length ? (
            <LoadingRow colSpan={COLS} />
          ) : error ? (
            <ErrorRow colSpan={COLS} message={error.message} />
          ) : (
            rows.map((a: AssetMaster) => (
              <tr key={a.id}>
                <td className="code">
                  <b>{a.code}</b>
                </td>
                <td>
                  {a.type === 'BUILDING' ? (
                    <span className="badge info">{assetTypeLabel(a.type)}</span>
                  ) : a.type === 'SIGNAGE' ? (
                    <span className="badge warning">{assetTypeLabel(a.type)}</span>
                  ) : (
                    <span className="badge neutral">{assetTypeLabel(a.type)}</span>
                  )}
                </td>
                <td>
                  <b>{a.nameTh}</b>
                </td>
                <td className="muted">{a.location ?? '—'}</td>
                <td className="muted">{a.size ?? '—'}</td>
                <td className="num tnum">{fmt(toNumber(a.value))}</td>
                <td className="muted">{a.ownerDept ?? '—'}</td>
                <td>{statusBadge(a.isActive)}</td>
                <RowActions
                  rowLabel={a.code}
                  onEdit={() => setEditingRow(a)}
                  onDelete={() => {
                    void deleteAsset.mutateAsync(a.id);
                  }}
                  deleting={deleteAsset.isPending}
                />
              </tr>
            ))
          )}
        </tbody>
      </table>
      {editingRow && (
        <AddMasterModal
          name="ทรัพย์สิน Master"
          sel="asset"
          mode="edit"
          initial={editingRow}
          onClose={() => setEditingRow(null)}
        />
      )}
    </div>
  );
}

function LoanList() {
  const companyId = useActiveCompanyId();
  const { data: rows = [], isPending, error } = useLoans(companyId);
  const deleteLoan = useDeleteLoan(companyId);
  const { map: projectMap } = useProjectNameMap(companyId);
  const [editingRow, setEditingRow] = useState<Loan | null>(null);
  const COLS = 9;
  return (
    <div className="tbl-shell">
      <table className="tbl">
        <thead>
          <tr>
            <th>รหัส</th>
            <th>ธนาคาร</th>
            <th className="num">เงินต้น</th>
            <th className="num">ดอกเบี้ย</th>
            <th>วัตถุประสงค์</th>
            <th>ผูกกับโครงการ</th>
            <th>ระยะเวลา</th>
            <th>สถานะ</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {isPending && !rows.length ? (
            <LoadingRow colSpan={COLS} />
          ) : error ? (
            <ErrorRow colSpan={COLS} message={error.message} />
          ) : (
            rows.map((l: Loan) => (
              <tr key={l.id}>
                <td className="code">
                  <b>{l.code}</b>
                </td>
                <td>{l.bank}</td>
                <td className="num tnum">{fmt(toNumber(l.principal))}</td>
                <td className="num">
                  <b>{toNumber(l.rate).toFixed(2)}%</b>
                </td>
                <td>
                  {l.purpose === 'SPECIFIC' ? (
                    <span className="badge brand">{loanPurposeLabel(l.purpose)}</span>
                  ) : (
                    <span className="badge info">{loanPurposeLabel(l.purpose)}</span>
                  )}
                </td>
                {/* tiedProjectId is a UUID — resolved against the projects list to its Thai name. */}
                <td className="muted">{resolveName(projectMap, l.tiedProjectId)}</td>
                <td className="muted">
                  {fmtDate(l.startDate)} → {fmtDate(l.endDate)}
                </td>
                <td>{statusBadge(l.isActive)}</td>
                <RowActions
                  rowLabel={l.code}
                  onEdit={() => setEditingRow(l)}
                  onDelete={() => {
                    void deleteLoan.mutateAsync(l.id);
                  }}
                  deleting={deleteLoan.isPending}
                />
              </tr>
            ))
          )}
        </tbody>
      </table>
      {editingRow && (
        <LoanFormModal mode="edit" initial={editingRow} onClose={() => setEditingRow(null)} />
      )}
    </div>
  );
}

function DocSeqList() {
  const companyId = useActiveCompanyId();
  const { data: docTypes = [], isPending, error } = useDocumentTypes(companyId);
  const [expanded, setExpanded] = useState<string | null>(null);
  const [showBookModal, setShowBookModal] = useState<DocumentType | null>(null);
  const [showAddBook, setShowAddBook] = useState<DocumentType | null>(null);
  const [editingDocType, setEditingDocType] = useState<DocumentType | null>(null);
  // Book-edit state lifted from DocSeqBookRows because a <tr> can't host a sibling
  // modal — keeping it here lets the modal render alongside the table.
  const [editingBook, setEditingBook] = useState<{
    book: VoucherBookWithCounter;
    docType: DocumentType;
  } | null>(null);
  const COLS = 9;

  return (
    <div className="tbl-shell">
      <div className="filter-row" style={{ marginBottom: 0 }}>
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          ประเภทเอกสาร · แต่ละประเภทสามารถมี &quot;เล่ม&quot; ย่อยได้หลายเล่ม
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 6, alignItems: 'center' }}>
          <span className="meta">{docTypes.length} ประเภท</span>
        </div>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th style={{ width: 32 }} />
            <th>รหัส</th>
            <th>ชื่อเอกสาร</th>
            <th>รูปแบบ</th>
            <th className="num">Pad Width</th>
            <th>การ Reset</th>
            <th>สถานะ</th>
            <th>ตัวอย่างเลขถัดไป</th>
            <th style={{ width: 140 }}>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {isPending && !docTypes.length ? (
            <LoadingRow colSpan={COLS} />
          ) : error ? (
            <ErrorRow colSpan={COLS} message={error.message} />
          ) : (
            docTypes.map((d) => {
              const isOpen = expanded === d.id;
              return (
                <Fragment key={d.id}>
                  <tr
                    onClick={() => setExpanded(isOpen ? null : d.id)}
                    style={{ cursor: 'pointer' }}
                  >
                    <td>
                      <span
                        style={{
                          display: 'inline-block',
                          transition: 'transform .15s',
                          transform: isOpen ? 'rotate(90deg)' : 'none',
                          fontSize: 12,
                          color: 'var(--text-muted)',
                        }}
                      >
                        ▶
                      </span>
                    </td>
                    <td className="code">
                      <b>{d.prefix}</b>
                    </td>
                    <td>{d.nameTh}</td>
                    <td className="code muted">{d.format}</td>
                    <td className="num tnum">{d.padWidth}</td>
                    <td className="muted">{resetLabel(d.resetRule)}</td>
                    <td>{statusBadge(d.isActive)}</td>
                    <td className="code muted">—</td>
                    <td onClick={(e) => e.stopPropagation()}>
                      <div style={{ display: 'flex', gap: 4 }}>
                        <button
                          className="btn sm"
                          style={{ height: 26, fontSize: 13 }}
                          onClick={() => setShowBookModal(d)}
                        >
                          จัดการเล่ม
                        </button>
                        <button
                          className="btn sm ghost"
                          style={{ height: 26, fontSize: 13 }}
                          title="แก้ไข"
                          onClick={() => setEditingDocType(d)}
                        >
                          <Icon name="settings" size={14} />
                        </button>
                      </div>
                    </td>
                  </tr>
                  {isOpen && (
                    <DocSeqBookRows
                      docType={d}
                      companyId={companyId}
                      onEditBook={(book) => setEditingBook({ book, docType: d })}
                    />
                  )}
                </Fragment>
              );
            })
          )}
        </tbody>
      </table>
      <div
        style={{
          padding: '12px 14px',
          background: '#fafafa',
          fontSize: 14,
          color: 'var(--text-muted)',
          lineHeight: 1.55,
          borderTop: '1px solid var(--border)',
        }}
      >
        <b>💡 หลักการ:</b> แต่ละประเภทเอกสารแบ่งเป็นหลาย &quot;เล่ม&quot; ได้ — กำหนด Range
        เลขแยกกันให้แต่ละทีม/สาขา/ประเภทธุรกรรม เลขจะเรียงต่อเนื่องในเล่มเดียวกัน ไม่ปะปนข้ามทีม ·
        ระบบเตือนเมื่อเล่มใกล้เต็ม + Auto สร้างเล่มใหม่ตาม Template
      </div>

      {showBookModal && (
        <BookManageModal
          docType={showBookModal}
          companyId={companyId}
          onClose={() => setShowBookModal(null)}
          onAddBook={() => {
            setShowAddBook(showBookModal);
            setShowBookModal(null);
          }}
          onEditBook={(book) => {
            setEditingBook({ book, docType: showBookModal });
          }}
        />
      )}
      {showAddBook && (
        <AddBookModal
          docType={showAddBook}
          companyId={companyId}
          onClose={() => setShowAddBook(null)}
        />
      )}
      {editingDocType && (
        <EditDocumentTypeModal
          docType={editingDocType}
          companyId={companyId}
          onClose={() => setEditingDocType(null)}
        />
      )}
      {editingBook && (
        <EditVoucherBookModal
          book={editingBook.book}
          docType={editingBook.docType}
          companyId={companyId}
          onClose={() => setEditingBook(null)}
        />
      )}
    </div>
  );
}

// Sub-component fetches books only for the currently expanded doc-type row,
// avoiding N+1 queries fired on initial mount of the table.
function DocSeqBookRows({
  docType,
  companyId,
  onEditBook,
}: {
  docType: DocumentType;
  companyId: string | undefined;
  onEditBook: (book: VoucherBookWithCounter) => void;
}) {
  const { data: books = [], isPending, error } = useVoucherBooks(companyId, docType.id);
  if (isPending && !books.length) {
    return (
      <tr style={{ background: '#fafafa' }}>
        <td />
        <td colSpan={8} className="muted" style={{ padding: 14, textAlign: 'center' }}>
          กำลังโหลดเล่ม…
        </td>
      </tr>
    );
  }
  if (error) {
    return (
      <tr style={{ background: '#fafafa' }}>
        <td />
        <td
          colSpan={8}
          className="muted"
          style={{ padding: 14, textAlign: 'center', color: 'var(--negative)' }}
        >
          โหลดเล่มไม่สำเร็จ: {error.message}
        </td>
      </tr>
    );
  }
  return (
    <>
      {books.map((b) => {
        // Counter values come from the live voucher_counters summary the API
        // attaches to each book; cap is the full range size.
        const totalCap = b.rangeEnd - b.rangeStart + 1;
        const used = b.counter.totalUsed;
        const pct = totalCap > 0 ? Math.round((used / totalCap) * 100) : 0;
        const next = b.rangeStart + used;
        return (
          <tr key={b.id} style={{ background: '#fafafa' }}>
            <td />
            <td className="code muted" style={{ fontSize: 13, paddingLeft: 24 }}>
              └ Book {b.bookCode}
            </td>
            <td>
              {b.nameTh}{' '}
              {b.team && (
                <span className="meta" style={{ marginLeft: 6 }}>
                  ({b.team})
                </span>
              )}
            </td>
            <td className="code muted">
              {fmtSeq(b.rangeStart)} – {fmtSeq(b.rangeEnd)}
            </td>
            <td className="num" style={{ minWidth: 140 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <div
                  className="progress-bar"
                  style={{
                    flex: 1,
                    height: 6,
                    borderRadius: 3,
                    background: '#e5e5e9',
                    overflow: 'hidden',
                  }}
                >
                  <div
                    style={{
                      width: `${Math.min(100, pct)}%`,
                      height: '100%',
                      background:
                        pct > 90
                          ? 'var(--negative)'
                          : pct > 70
                            ? 'var(--warning)'
                            : 'var(--positive)',
                    }}
                  />
                </div>
                <span style={{ fontSize: 13, minWidth: 34, textAlign: 'right' }}>{pct}%</span>
              </div>
            </td>
            <td className="num tnum">
              {fmt(used)} / {fmt(totalCap)}
            </td>
            <td className="muted">
              เลขถัด:{' '}
              <b className="code" style={{ color: 'var(--brand)' }}>
                {fmtSeq(next)}
              </b>
            </td>
            <td className="code">{bookSample(docType, b, next)}</td>
            <td>
              <button
                className="btn sm ghost"
                style={{ height: 26, fontSize: 13 }}
                title="แก้ไข"
                onClick={() => onEditBook(b)}
              >
                <Icon name="settings" size={14} />
              </button>
            </td>
          </tr>
        );
      })}
    </>
  );
}

interface BookManageModalProps {
  docType: DocumentType;
  companyId: string | undefined;
  onClose: () => void;
  onAddBook: () => void;
  onEditBook: (book: VoucherBookWithCounter) => void;
}

function BookManageModal({
  docType,
  companyId,
  onClose,
  onAddBook,
  onEditBook,
}: BookManageModalProps) {
  const { data: books = [], isPending, error } = useVoucherBooks(companyId, docType.id);
  // Soft-delete via PATCH isActive=false on the API side ("ปิดเล่ม" = close, not erase).
  const deleteBook = useDeleteVoucherBook(companyId, docType.id);
  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 200,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 980,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
          maxHeight: '94vh',
          display: 'flex',
          flexDirection: 'column',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h3
              style={{
                margin: 0,
                fontSize: 18,
                fontWeight: 600,
                fontFamily: 'var(--font-display)',
              }}
            >
              จัดการเล่ม — {docType.prefix} {docType.nameTh}
            </h3>
            <div className="meta" style={{ marginTop: 2 }}>
              รูปแบบ <span className="code">{docType.format}</span> · Reset{' '}
              {resetLabel(docType.resetRule)} · {books.length} เล่ม
            </div>
          </div>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '18px 22px', overflow: 'auto', flex: 1 }}>
          <div
            style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              marginBottom: 12,
            }}
          >
            <div className="section-title" style={{ margin: 0 }}>
              เล่มทั้งหมด
            </div>
            <button className="btn sm primary" onClick={onAddBook}>
              <Icon name="plus" size={12} /> เพิ่มเล่มใหม่
            </button>
          </div>
          {isPending && !books.length ? (
            <div className="muted" style={{ padding: 24, textAlign: 'center' }}>
              กำลังโหลด…
            </div>
          ) : error ? (
            <div
              className="muted"
              style={{ padding: 24, textAlign: 'center', color: 'var(--negative)' }}
            >
              ไม่สามารถโหลดข้อมูล: {error.message}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
              {books.map((b) => {
                const cap = b.rangeEnd - b.rangeStart + 1;
                const used = b.counter.totalUsed;
                const usedPct = cap > 0 ? (used / cap) * 100 : 0;
                const remain = Math.max(0, cap - used);
                const next = b.rangeStart + used;
                const lowCap = remain < cap * 0.1;
                return (
                  <div
                    key={b.id}
                    style={{
                      border: '1px solid var(--border)',
                      borderRadius: 8,
                      padding: '14px 16px',
                      background: lowCap ? '#fef3e8' : 'white',
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 10,
                      }}
                    >
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                          <span className="badge brand" style={{ fontSize: 13 }}>
                            Book {b.bookCode}
                          </span>
                          <b style={{ fontSize: 15 }}>{b.nameTh}</b>
                          {b.isActive && (
                            <span className="badge positive" style={{ fontSize: 13 }}>
                              Active
                            </span>
                          )}
                          {lowCap && (
                            <span className="badge warning" style={{ fontSize: 13 }}>
                              ⚠ ใกล้เต็ม
                            </span>
                          )}
                        </div>
                        <div className="meta" style={{ marginTop: 4 }}>
                          ทีม/หน่วยงาน: <b style={{ color: '#3f3f46' }}>{b.team ?? '—'}</b>
                        </div>
                      </div>
                      <div style={{ display: 'flex', gap: 6 }}>
                        <button
                          className="btn sm ghost"
                          style={{ height: 28, fontSize: 13 }}
                          onClick={() => onEditBook(b)}
                        >
                          แก้ไข
                        </button>
                        <button
                          className="btn sm ghost"
                          style={{ height: 28, fontSize: 13, color: 'var(--negative)' }}
                          disabled={deleteBook.isPending}
                          onClick={() => {
                            if (confirm('ยืนยันปิดเล่ม ' + b.bookCode + '?')) {
                              deleteBook.mutate(b.id);
                            }
                          }}
                        >
                          ปิดเล่ม
                        </button>
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: '1fr 1fr 1fr 1fr',
                        gap: 14,
                        marginBottom: 10,
                      }}
                    >
                      <div>
                        <div className="meta">Range เริ่ม</div>
                        <div
                          className="code"
                          style={{ fontWeight: 600, fontSize: 15, marginTop: 2 }}
                        >
                          {fmtSeq(b.rangeStart)}
                        </div>
                      </div>
                      <div>
                        <div className="meta">Range สิ้นสุด</div>
                        <div
                          className="code"
                          style={{ fontWeight: 600, fontSize: 15, marginTop: 2 }}
                        >
                          {fmtSeq(b.rangeEnd)}
                        </div>
                      </div>
                      <div>
                        <div className="meta">เลขปัจจุบัน</div>
                        <div
                          className="code"
                          style={{
                            fontWeight: 600,
                            fontSize: 15,
                            color: 'var(--brand)',
                            marginTop: 2,
                          }}
                        >
                          {fmtSeq(next)}
                        </div>
                      </div>
                      <div>
                        <div className="meta">ใช้ไป / ความจุ</div>
                        <div
                          style={{ fontWeight: 600, fontSize: 15, marginTop: 2 }}
                          className="tnum"
                        >
                          {fmt(used)} / {fmt(cap)}
                        </div>
                      </div>
                    </div>
                    <div
                      style={{
                        height: 8,
                        borderRadius: 4,
                        background: '#f0f0f3',
                        overflow: 'hidden',
                      }}
                    >
                      <div
                        style={{
                          width: `${Math.min(100, usedPct)}%`,
                          height: '100%',
                          background:
                            usedPct > 90
                              ? 'var(--negative)'
                              : usedPct > 70
                                ? 'var(--warning)'
                                : 'var(--positive)',
                        }}
                      />
                    </div>
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        marginTop: 6,
                        fontSize: 13,
                        color: 'var(--text-muted)',
                      }}
                    >
                      <span>{usedPct.toFixed(2)}% ใช้ไปแล้ว</span>
                      <span>
                        คงเหลือ {fmt(remain)} เลข · เลขถัดไป{' '}
                        <b className="code" style={{ color: 'var(--brand)' }}>
                          {bookSample(docType, b, next)}
                        </b>
                      </span>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'space-between',
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ปิด
          </button>
          <button className="btn primary" onClick={onAddBook}>
            <Icon name="plus" size={14} /> เพิ่มเล่มใหม่
          </button>
        </div>
      </div>
    </div>
  );
}

function AddBookModal({
  docType,
  companyId,
  onClose,
}: {
  docType: DocumentType;
  companyId: string | undefined;
  onClose: () => void;
}) {
  const { data: books = [] } = useVoucherBooks(companyId, docType.id);
  const createBook = useCreateVoucherBook(companyId, docType.id);
  const lastEnd = books.length ? Math.max(...books.map((b) => b.rangeEnd)) : 0;
  const nextStart = lastEnd + 1;
  const defaultEnd = nextStart + 49999;

  const [bookCode, setBookCode] = useState(String(books.length + 1).padStart(2, '0'));
  const [nameTh, setNameTh] = useState('');
  const [rangeStart, setRangeStart] = useState(nextStart);
  const [rangeEnd, setRangeEnd] = useState(defaultEnd);
  const [team, setTeam] = useState('');
  const [routingRule, setRoutingRule] = useState('Auto · ตามสาขาของผู้ใช้');

  async function handleSubmit() {
    if (!nameTh) return;
    const input: Omit<CreateVoucherBook, 'docTypeId'> = {
      bookCode,
      nameTh,
      rangeStart,
      rangeEnd,
      team: team || null,
      routingRule: routingRule || null,
      isActive: true,
    };
    await createBook.mutateAsync(input);
    onClose();
  }

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 200,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 640,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              fontFamily: 'var(--font-display)',
            }}
          >
            เพิ่มเล่มใหม่ — {docType.prefix} {docType.nameTh}
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: 14 }}>
          <div style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                รหัสเล่ม
              </label>
              <input
                className="input code"
                value={bookCode}
                onChange={(e) => setBookCode(e.target.value)}
                style={{ width: 80, height: 38, fontSize: 14, textAlign: 'center' }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                ชื่อเล่ม
              </label>
              <input
                className="input"
                placeholder="เช่น รับชำระลูกหนี้ — ทีมภาคเหนือ"
                value={nameTh}
                onChange={(e) => setNameTh(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>

          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14 }}>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Range เริ่ม
              </label>
              <input
                className="input code tnum"
                type="number"
                value={rangeStart}
                onChange={(e) => setRangeStart(Number(e.target.value))}
                style={{
                  width: '100%',
                  height: 38,
                  fontSize: 15,
                  textAlign: 'right',
                  fontWeight: 600,
                }}
              />
            </div>
            <div>
              <label
                style={{
                  fontSize: 14,
                  color: 'var(--text-muted)',
                  fontWeight: 500,
                  display: 'block',
                  marginBottom: 6,
                }}
              >
                Range สิ้นสุด
              </label>
              <input
                className="input code tnum"
                type="number"
                value={rangeEnd}
                onChange={(e) => setRangeEnd(Number(e.target.value))}
                style={{
                  width: '100%',
                  height: 38,
                  fontSize: 15,
                  textAlign: 'right',
                  fontWeight: 600,
                }}
              />
            </div>
          </div>
          <div className="meta">
            💡 แนะนำให้กำหนด Range ห่างกัน 10,000 - 100,000 เลข ต่อเล่ม เพื่อความยืดหยุ่น
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              ทีม/หน่วยงานที่ใช้
            </label>
            <input
              className="input"
              placeholder="เช่น ทีมขายภาคเหนือ, สาขาเชียงใหม่ทุกสาขา"
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>

          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 8,
              }}
            >
              Routing Rule — เกณฑ์การเลือกเล่มอัตโนมัติ
            </label>
            <select
              className="select"
              value={routingRule}
              onChange={(e) => setRoutingRule(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            >
              <option>Auto · ตามสาขาของผู้ใช้</option>
              <option>Auto · ตามแผนกของผู้ใช้</option>
              <option>Auto · ตามประเภทธุรกรรม</option>
              <option>Auto · ตาม Customer Segment</option>
              <option>Manual · ให้ผู้ใช้เลือกเอง</option>
              <option>Hybrid · ระบบแนะนำ + แก้ได้</option>
            </select>
          </div>

          <div
            style={{
              padding: '12px 14px',
              background: '#e8eff8',
              borderRadius: 6,
              fontSize: 14,
              lineHeight: 1.55,
              display: 'flex',
              gap: 10,
            }}
          >
            <div
              style={{
                width: 22,
                height: 22,
                borderRadius: '50%',
                background: 'var(--info)',
                color: 'white',
                display: 'grid',
                placeItems: 'center',
                fontSize: 14,
                fontWeight: 600,
                flexShrink: 0,
              }}
            >
              i
            </div>
            <div>
              เมื่อสร้างเล่ม ระบบจะ: (1) ตรวจสอบว่า Range ไม่ทับกับเล่มอื่น (2) สร้าง Counter
              เริ่มต้น (3) Apply Routing Rule (4) บันทึก Audit Log
            </div>
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={createBook.isPending || !nameTh}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="check" size={14} /> {createBook.isPending ? 'กำลังบันทึก…' : 'สร้างเล่ม'}
          </button>
        </div>
      </div>
    </div>
  );
}

function PeriodList() {
  const companyId = useActiveCompanyId();
  const { data: rows = [], isPending, error } = usePeriods(companyId);
  const updatePeriod = useUpdatePeriod(companyId);
  const [editingPeriod, setEditingPeriod] = useState<PeriodMaster | null>(null);
  const COLS = 6;

  async function transition(p: PeriodMaster, next: 'OPEN' | 'CLOSED' | 'FUTURE') {
    await updatePeriod.mutateAsync({
      id: p.id,
      data: {
        status: next,
        closedBy: next === 'CLOSED' ? 'system' : null,
        closedDate: next === 'CLOSED' ? new Date().toISOString() : null,
      },
    });
  }

  return (
    <div className="tbl-shell">
      <table className="tbl">
        <thead>
          <tr>
            <th>งวด</th>
            <th>ชื่อ</th>
            <th>สถานะ</th>
            <th>ผู้ปิดงวด</th>
            <th>วันที่ปิด</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {isPending && !rows.length ? (
            <LoadingRow colSpan={COLS} />
          ) : error ? (
            <ErrorRow colSpan={COLS} message={error.message} />
          ) : (
            rows.map((p) => (
              <tr key={p.id}>
                <td className="code">
                  <b>{periodCodeToDisplay(p.period)}</b>
                </td>
                <td>{p.nameTh}</td>
                <td>
                  {p.status === 'CLOSED' ? (
                    <span className="badge neutral">
                      <span className="bdot" />
                      ปิดงวดแล้ว
                    </span>
                  ) : p.status === 'OPEN' ? (
                    <span className="badge positive">
                      <span className="bdot" />
                      เปิดอยู่
                    </span>
                  ) : (
                    <span className="badge info">
                      <span className="bdot" />
                      ยังไม่เริ่ม
                    </span>
                  )}
                </td>
                <td className="muted">{p.closedBy ?? '—'}</td>
                <td className="muted">{p.closedDate ? fmtDate(p.closedDate) : '—'}</td>
                <td>
                  <div style={{ display: 'flex', gap: 4 }}>
                    {p.status === 'OPEN' ? (
                      <button
                        className="btn sm primary"
                        style={{ height: 26, fontSize: 13 }}
                        disabled={updatePeriod.isPending}
                        onClick={() => {
                          void transition(p, 'CLOSED');
                        }}
                      >
                        ปิดงวด
                      </button>
                    ) : p.status === 'CLOSED' ? (
                      <button
                        className="btn sm ghost"
                        style={{ height: 26, fontSize: 13 }}
                        disabled={updatePeriod.isPending}
                        onClick={() => {
                          void transition(p, 'OPEN');
                        }}
                      >
                        เปิดงวดใหม่
                      </button>
                    ) : (
                      <button
                        className="btn sm ghost"
                        style={{ height: 26, fontSize: 13 }}
                        disabled={updatePeriod.isPending}
                        onClick={() => {
                          void transition(p, 'OPEN');
                        }}
                      >
                        เปิดงวด
                      </button>
                    )}
                    <button
                      className="btn sm ghost"
                      style={{ height: 26, fontSize: 13 }}
                      title="แก้ไข"
                      onClick={() => setEditingPeriod(p)}
                    >
                      <Icon name="settings" size={14} />
                    </button>
                  </div>
                </td>
              </tr>
            ))
          )}
        </tbody>
      </table>
      {editingPeriod && (
        <EditPeriodModal
          period={editingPeriod}
          companyId={companyId}
          onClose={() => setEditingPeriod(null)}
        />
      )}
    </div>
  );
}

function CurrencyList() {
  const { data: rows = [], isPending, error } = useCurrencies();
  const [editingRow, setEditingRow] = useState<CurrencyMaster | null>(null);
  const COLS = 7;
  return (
    <div className="tbl-shell">
      <div className="filter-row">
        <span style={{ fontSize: 14, fontWeight: 600 }}>
          อัตราแลกเปลี่ยน · อัปเดตล่าสุด 23 พ.ค. 2026 09:00
        </span>
        <div style={{ marginLeft: 'auto' }}>
          <button
            className="btn sm"
            onClick={() => {
              /* TODO(phase-3c.1): wire ธปท. rate sync */
            }}
          >
            <Icon name="refresh" size={12} /> Update จาก ธปท.
          </button>
        </div>
      </div>
      <table className="tbl">
        <thead>
          <tr>
            <th>รหัส</th>
            <th>ชื่อสกุลเงิน</th>
            <th>สัญลักษณ์</th>
            <th className="num">อัตราแลกเปลี่ยน (THB)</th>
            <th>อัปเดตล่าสุด</th>
            <th>Base Currency</th>
            <th>การกระทำ</th>
          </tr>
        </thead>
        <tbody>
          {isPending && !rows.length ? (
            <LoadingRow colSpan={COLS} />
          ) : error ? (
            <ErrorRow colSpan={COLS} message={error.message} />
          ) : (
            rows.map((c: CurrencyMaster) => (
              <tr key={c.code}>
                <td className="code">
                  <b>{c.code}</b>
                </td>
                <td>{c.nameTh}</td>
                <td style={{ fontSize: 18 }}>{c.symbol}</td>
                <td className="num tnum">{toNumber(c.rate).toFixed(4)}</td>
                <td className="muted">{fmtDate(c.lastUpdate)}</td>
                <td>
                  {c.isBase ? (
                    <span className="badge brand">
                      <Icon name="check" size={10} /> Base
                    </span>
                  ) : (
                    <span className="muted">—</span>
                  )}
                </td>
                {/* Currency supports PATCH /currencies/:code only — DELETE returns 405, so
                    the row exposes only an Edit action (no soft-delete). */}
                <RowActions rowLabel={c.code} onEdit={() => setEditingRow(c)} />
              </tr>
            ))
          )}
        </tbody>
      </table>
      {editingRow && (
        <AddMasterModal
          name="สกุลเงิน"
          sel="currency"
          mode="edit"
          initial={editingRow}
          onClose={() => setEditingRow(null)}
        />
      )}
    </div>
  );
}

// Row shape passed into the modal for edit mode. We only read `id`, `code`,
// and the display name here — the rest stays untouched on the server
// (partial PATCH). Most masters call the display field `nameTh`; Loan calls
// it `bank`. Employee also has a `bank` (string|null) field — irrelevant for
// the modal's "display name" purpose but it must structurally fit
// EditableRow under exactOptionalPropertyTypes, so we allow null|undefined.
// Currency uses `code` as the natural URL key instead of `id`.
interface EditableRow {
  id?: string | undefined;
  code: string;
  nameTh?: string | null | undefined;
  bank?: string | null | undefined;
}

interface AddMasterModalProps {
  name: string;
  sel: MasterId;
  mode: 'add' | 'edit';
  initial?: EditableRow;
  onClose: () => void;
}

// Generic add+edit modal — translates (code, name) → the most permissive
// Create<X>/Update<X> payload for the currently-selected master. Tables with
// richer fields (Branch full address, Vendor terms, etc.) still need per-table
// modals in phase-3c.2; this generic form covers the simple cases.
function AddMasterModal({ name, sel, mode, initial, onClose }: AddMasterModalProps) {
  const companyId = useActiveCompanyId();
  const [code, setCode] = useState(initial?.code ?? '');
  // Loan stores its display label in `bank`; everything else uses `nameTh`.
  // For Employee the `bank` field is a bank-account string (unrelated) — we
  // only fall back to `bank` when `nameTh` is absent, which only happens for
  // Loan rows.
  const [valueTh, setValueTh] = useState(initial?.nameTh ?? initial?.bank ?? '');
  const [error, setError] = useState<string | null>(null);

  const createBranch = useCreateBranch(companyId);
  const createDept = useCreateDepartment(companyId);
  const createProject = useCreateProject(companyId);
  const createEmployee = useCreateEmployee(companyId);
  const createVendor = useCreateVendor(companyId);
  const createCustomer = useCreateCustomer(companyId);
  const createBank = useCreateBankAccount(companyId);
  const createAsset = useCreateAssetMaster(companyId);
  const createLoan = useCreateLoan(companyId);
  const createCurrency = useCreateCurrency();

  const updateBranch = useUpdateBranch(companyId);
  const updateDept = useUpdateDepartment(companyId);
  const updateProject = useUpdateProject(companyId);
  const updateEmployee = useUpdateEmployee(companyId);
  const updateVendor = useUpdateVendor(companyId);
  const updateCustomer = useUpdateCustomer(companyId);
  const updateBank = useUpdateBankAccount(companyId);
  const updateAsset = useUpdateAssetMaster(companyId);
  const updateLoan = useUpdateLoan(companyId);
  const updateCurrency = useUpdateCurrency();

  const submitting =
    createBranch.isPending ||
    createDept.isPending ||
    createProject.isPending ||
    createEmployee.isPending ||
    createVendor.isPending ||
    createCustomer.isPending ||
    createBank.isPending ||
    createAsset.isPending ||
    createLoan.isPending ||
    createCurrency.isPending ||
    updateBranch.isPending ||
    updateDept.isPending ||
    updateProject.isPending ||
    updateEmployee.isPending ||
    updateVendor.isPending ||
    updateCustomer.isPending ||
    updateBank.isPending ||
    updateAsset.isPending ||
    updateLoan.isPending ||
    updateCurrency.isPending;

  async function handleAdd() {
    if (sel === 'branch') {
      const input: Omit<CreateBranch, 'companyId'> = {
        code,
        nameTh: valueTh,
        taxBranch: '00000',
        address: null,
        isActive: true,
      };
      await createBranch.mutateAsync(input);
    } else if (sel === 'dept') {
      const input: Omit<CreateDepartment, 'companyId'> = {
        code,
        nameTh: valueTh,
        shortCode: code,
        costCenter: null,
        head: null,
        headcount: 0,
        isActive: true,
      };
      await createDept.mutateAsync(input);
    } else if (sel === 'project') {
      const input: Omit<CreateProject, 'companyId'> = {
        code,
        nameTh: valueTh,
        type: 'CIP',
        branchId: null,
        pm: null,
        budget: 0,
        isActive: true,
      };
      await createProject.mutateAsync(input);
    } else if (sel === 'employee') {
      const input: Omit<CreateEmployee, 'companyId'> = {
        code,
        nameTh: valueTh,
        position: '—',
        email: null,
        departmentId: null,
        joined: new Date().toISOString().slice(0, 10),
        bank: null,
        isActive: true,
      };
      await createEmployee.mutateAsync(input);
    } else if (sel === 'vendor') {
      // @ts-expect-error TRANSITIONAL (M2 Plan A): a Vendor is now a BRANCH under a
      // Supplier (requires supplierId/branchCode). This legacy /master vendor-create
      // is superseded by the new /suppliers master screen (M2 Plan B) — rework or
      // remove it there; marker removed once it routes through the supplier flow.
      const input: Omit<CreateVendor, 'companyId'> = {
        code,
        nameTh: valueTh,
        taxId: null,
        country: 'TH',
        terms: null,
        email: null,
        currency: 'THB',
        isActive: true,
      };
      await createVendor.mutateAsync(input);
    } else if (sel === 'customer') {
      const input: Omit<CreateCustomer, 'companyId'> = {
        code,
        nameTh: valueTh,
        taxId: null,
        terms: null,
        creditLimit: 0,
        ytdSales: 0,
        isActive: true,
      };
      await createCustomer.mutateAsync(input);
    } else if (sel === 'bank') {
      const input: Omit<CreateBankAccount, 'companyId'> = {
        code,
        bank: valueTh || code,
        accNo: '—',
        nameTh: valueTh,
        type: 'CURRENT',
        branch: null,
        balance: 0,
        isActive: true,
      };
      await createBank.mutateAsync(input);
    } else if (sel === 'asset') {
      const input: Omit<CreateAssetMaster, 'companyId'> = {
        code,
        nameTh: valueTh,
        type: 'BUILDING',
        location: null,
        size: null,
        value: 0,
        ownerDept: null,
        isActive: true,
      };
      await createAsset.mutateAsync(input);
    } else if (sel === 'loan') {
      const today = new Date().toISOString().slice(0, 10);
      const input: Omit<CreateLoan, 'companyId'> = {
        code,
        bank: valueTh || code,
        principal: 0,
        rate: 0,
        purpose: 'GENERAL',
        tiedProjectId: null,
        startDate: today,
        endDate: today,
        isActive: true,
      };
      await createLoan.mutateAsync(input);
    } else if (sel === 'currency') {
      // Currency code must be 3-letter ISO 4217 uppercase.
      const input: CreateCurrencyMaster = {
        code: code.toUpperCase(),
        nameTh: valueTh,
        symbol: code.toUpperCase(),
        rate: 1,
        isBase: false,
        lastUpdate: new Date().toISOString(),
      };
      await createCurrency.mutateAsync(input);
    } else {
      // sel === 'coa' | 'company' | 'doc' | 'period' — richer modals deferred to wave 3.
      setError('ฟอร์มสำหรับ master นี้ยังไม่รองรับ — ใช้เครื่องมือเฉพาะของแต่ละตาราง');
      return false;
    }
    return true;
  }

  async function handleEdit() {
    if (!initial) return false;
    // Currency uses `code` (natural key, immutable on PATCH) and a smaller
    // update shape than its full Create payload — handle it before the id-keyed
    // family below.
    if (sel === 'currency') {
      const data: UpdateCurrencyMaster = { nameTh: valueTh };
      await updateCurrency.mutateAsync({ code: initial.code, data });
      return true;
    }
    const id = initial.id;
    if (!id) {
      setError('ไม่พบ id ของแถวนี้');
      return false;
    }
    // Partial PATCH — only ship the two fields the generic modal owns; all
    // other columns on the row stay untouched on the server.
    if (sel === 'branch') {
      const data: UpdateBranch = { code, nameTh: valueTh };
      await updateBranch.mutateAsync({ id, data });
    } else if (sel === 'dept') {
      const data: UpdateDepartment = { code, nameTh: valueTh };
      await updateDept.mutateAsync({ id, data });
    } else if (sel === 'project') {
      const data: UpdateProject = { code, nameTh: valueTh };
      await updateProject.mutateAsync({ id, data });
    } else if (sel === 'employee') {
      const data: UpdateEmployee = { code, nameTh: valueTh };
      await updateEmployee.mutateAsync({ id, data });
    } else if (sel === 'vendor') {
      const data: UpdateVendor = { code, nameTh: valueTh };
      await updateVendor.mutateAsync({ id, data });
    } else if (sel === 'customer') {
      const data: UpdateCustomer = { code, nameTh: valueTh };
      await updateCustomer.mutateAsync({ id, data });
    } else if (sel === 'bank') {
      const data: UpdateBankAccount = { code, nameTh: valueTh };
      await updateBank.mutateAsync({ id, data });
    } else if (sel === 'asset') {
      const data: UpdateAssetMaster = { code, nameTh: valueTh };
      await updateAsset.mutateAsync({ id, data });
    } else if (sel === 'loan') {
      const data: UpdateLoan = { code, bank: valueTh };
      await updateLoan.mutateAsync({ id, data });
    } else {
      setError('ฟอร์มแก้ไขสำหรับ master นี้ยังไม่รองรับ');
      return false;
    }
    return true;
  }

  async function handleSubmit() {
    setError(null);
    try {
      const ok = mode === 'edit' ? await handleEdit() : await handleAdd();
      if (ok) onClose();
    } catch (e) {
      const msg = e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ';
      setError(msg);
    }
  }

  const disabled = !code || !valueTh || submitting;

  return (
    <div
      style={{
        position: 'fixed',
        inset: 0,
        background: 'rgba(15,15,20,0.45)',
        display: 'grid',
        placeItems: 'center',
        zIndex: 100,
        padding: 24,
      }}
      onClick={onClose}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: 'white',
          borderRadius: 12,
          width: 560,
          maxWidth: '100%',
          boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
          overflow: 'hidden',
        }}
      >
        <div
          style={{
            padding: '18px 22px',
            borderBottom: '1px solid var(--border)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <h3
            style={{
              margin: 0,
              fontSize: 18,
              fontWeight: 600,
              fontFamily: 'var(--font-display)',
            }}
          >
            {mode === 'edit' ? 'แก้ไข' : 'เพิ่มข้อมูล'} — {name}
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={{ padding: '22px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              รหัส (Code)
            </label>
            <input
              className="input code"
              placeholder="ระบบจะ Auto-gen หรือพิมพ์เอง"
              value={code}
              onChange={(e) => setCode(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          <div>
            <label
              style={{
                fontSize: 14,
                color: 'var(--text-muted)',
                fontWeight: 500,
                display: 'block',
                marginBottom: 6,
              }}
            >
              ชื่อ
            </label>
            <input
              className="input"
              placeholder="ชื่อข้อมูลที่จะเพิ่ม"
              value={valueTh}
              onChange={(e) => setValueTh(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          {error && (
            <div
              style={{
                padding: '10px 12px',
                background: '#fdecec',
                color: 'var(--negative)',
                borderRadius: 6,
                fontSize: 14,
              }}
            >
              {error}
            </div>
          )}
          <div
            style={{
              padding: '12px 14px',
              background: '#fafafa',
              borderRadius: 6,
              fontSize: 14,
              color: 'var(--text-muted)',
            }}
          >
            <b>หมายเหตุ:</b> ฟอร์มจะปรับให้ตรงกับประเภท master ที่เลือก · ระบบ validate รหัสไม่ซ้ำ
            และ Tax ID 13 หลัก · บันทึก Activity Log อัตโนมัติ
          </div>
        </div>
        <div
          style={{
            padding: '14px 22px',
            borderTop: '1px solid var(--border)',
            display: 'flex',
            justifyContent: 'flex-end',
            gap: 8,
            background: '#fafafa',
          }}
        >
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={disabled}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Dedicated Branch + Department forms — expose every editable
// field (taxBranch, costCenter, etc.) instead of the generic
// 2-field AddMasterModal. Create + edit share one component;
// `code` is immutable on edit (natural key → URL identity stays
// stable). `companyId` is supplied by the create mutation hook,
// so the payload omits it.
// ============================================================

interface BranchFormModalProps {
  mode: 'add' | 'edit';
  initial?: Branch;
  onClose: () => void;
}

function BranchFormModal({ mode, initial, onClose }: BranchFormModalProps) {
  const companyId = useActiveCompanyId();
  const createBranch = useCreateBranch(companyId);
  const updateBranch = useUpdateBranch(companyId);

  const [code, setCode] = useState(initial?.code ?? '');
  const [nameTh, setNameTh] = useState(initial?.nameTh ?? '');
  const [taxBranch, setTaxBranch] = useState(initial?.taxBranch ?? '00000');
  const [address, setAddress] = useState(initial?.address ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const submitting = createBranch.isPending || updateBranch.isPending;
  // taxBranch must be exactly 5 digits (matches shared BranchSchema regex).
  const taxBranchValid = /^\d{5}$/.test(taxBranch);
  const disabled = !code || !nameTh || !taxBranchValid || submitting;

  async function handleSubmit() {
    setError(null);
    try {
      if (mode === 'edit') {
        if (!initial) {
          setError('ไม่พบข้อมูลสาขา');
          return;
        }
        // code is immutable on edit — omit it from the partial PATCH.
        const data: UpdateBranch = {
          nameTh,
          taxBranch,
          address: address || null,
          isActive,
        };
        await updateBranch.mutateAsync({ id: initial.id, data });
      } else {
        const input: Omit<CreateBranch, 'companyId'> = {
          code,
          nameTh,
          taxBranch,
          address: address || null,
          isActive,
        };
        await createBranch.mutateAsync(input);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <div style={editModalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={editModalBox}>
        <div style={editModalHeader}>
          <h3 style={editModalTitle}>{mode === 'edit' ? 'แก้ไขสาขา' : 'เพิ่มสาขา'}</h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={editModalBody}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>รหัสสาขา</label>
              <input
                className="input code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                readOnly={mode === 'edit'}
                placeholder="เช่น BR-001"
                style={{
                  width: '100%',
                  height: 38,
                  fontSize: 14,
                  background: mode === 'edit' ? '#fafafa' : undefined,
                }}
              />
            </div>
            <div>
              <label style={editModalLabel}>ลำดับที่ของสาขา (5 หลัก)</label>
              <input
                className="input code tnum"
                value={taxBranch}
                onChange={(e) => setTaxBranch(e.target.value)}
                placeholder="00000"
                maxLength={5}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
              {!taxBranchValid && (
                <div style={{ fontSize: 13, color: 'var(--negative)', marginTop: 4 }}>
                  ต้องเป็นตัวเลข 5 หลัก (00000 = สำนักงานใหญ่)
                </div>
              )}
            </div>
          </div>
          <div>
            <label style={editModalLabel}>ชื่อสาขา</label>
            <input
              className="input"
              value={nameTh}
              onChange={(e) => setNameTh(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          <div>
            <label style={editModalLabel}>ที่อยู่</label>
            <input
              className="input"
              value={address}
              onChange={(e) => setAddress(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            ใช้งานอยู่
          </label>
          {error && <div style={editModalError}>บันทึกไม่สำเร็จ: {error}</div>}
        </div>
        <div style={editModalFooter}>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={disabled}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface DepartmentFormModalProps {
  mode: 'add' | 'edit';
  initial?: Department;
  onClose: () => void;
}

function DepartmentFormModal({ mode, initial, onClose }: DepartmentFormModalProps) {
  const companyId = useActiveCompanyId();
  const createDept = useCreateDepartment(companyId);
  const updateDept = useUpdateDepartment(companyId);

  const [code, setCode] = useState(initial?.code ?? '');
  const [nameTh, setNameTh] = useState(initial?.nameTh ?? '');
  const [shortCode, setShortCode] = useState(initial?.shortCode ?? '');
  const [branchId, setBranchId] = useState(initial?.branchId ?? '');
  const [costCenter, setCostCenter] = useState(initial?.costCenter ?? '');
  const [head, setHead] = useState(initial?.head ?? '');
  const [headcount, setHeadcount] = useState<number>(initial?.headcount ?? 0);
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const submitting = createDept.isPending || updateDept.isPending;
  const disabled = !code || !nameTh || !shortCode || headcount < 0 || submitting;

  async function handleSubmit() {
    setError(null);
    try {
      if (mode === 'edit') {
        if (!initial) {
          setError('ไม่พบข้อมูลแผนก');
          return;
        }
        // code is immutable on edit — omit it from the partial PATCH.
        const data: UpdateDepartment = {
          nameTh,
          shortCode,
          branchId: branchId || null,
          costCenter: costCenter || null,
          head: head || null,
          headcount,
          isActive,
        };
        await updateDept.mutateAsync({ id: initial.id, data });
      } else {
        const input: Omit<CreateDepartment, 'companyId'> = {
          code,
          nameTh,
          shortCode,
          branchId: branchId || null,
          costCenter: costCenter || null,
          head: head || null,
          headcount,
          isActive,
        };
        await createDept.mutateAsync(input);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <div style={editModalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={editModalBox}>
        <div style={editModalHeader}>
          <h3 style={editModalTitle}>{mode === 'edit' ? 'แก้ไขแผนก' : 'เพิ่มแผนก'}</h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={editModalBody}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>รหัสแผนก</label>
              <input
                className="input code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                readOnly={mode === 'edit'}
                placeholder="เช่น DEPT-01"
                style={{
                  width: '100%',
                  height: 38,
                  fontSize: 14,
                  background: mode === 'edit' ? '#fafafa' : undefined,
                }}
              />
            </div>
            <div>
              <label style={editModalLabel}>รหัสย่อ</label>
              <input
                className="input code"
                value={shortCode}
                onChange={(e) => setShortCode(e.target.value)}
                placeholder="เช่น ACC"
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>
          <div>
            <label style={editModalLabel}>ชื่อแผนก</label>
            <input
              className="input"
              value={nameTh}
              onChange={(e) => setNameTh(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          <div>
            <label style={editModalLabel}>สาขาที่สังกัด (Cost Center)</label>
            <BranchSelect
              companyId={companyId}
              value={branchId}
              onChange={setBranchId}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>Cost Center</label>
              <input
                className="input code"
                value={costCenter}
                onChange={(e) => setCostCenter(e.target.value)}
                placeholder="ไม่บังคับ"
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={editModalLabel}>จำนวนพนักงาน</label>
              <input
                className="input tnum"
                type="number"
                min={0}
                value={headcount}
                onChange={(e) => setHeadcount(Number(e.target.value))}
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
          </div>
          <div>
            <label style={editModalLabel}>หัวหน้าแผนก</label>
            <input
              className="input"
              value={head}
              onChange={(e) => setHead(e.target.value)}
              placeholder="ไม่บังคับ"
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            ใช้งานอยู่
          </label>
          {error && <div style={editModalError}>บันทึกไม่สำเร็จ: {error}</div>}
        </div>
        <div style={editModalFooter}>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={disabled}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// BankFormModal — full bank-account create/edit form. Replaces the generic
// 2-field modal, which could only set code + name (account number, type,
// branch, and opening balance fell back to placeholders: accNo '—', CURRENT, 0).
// ============================================================

interface BankFormModalProps {
  mode: 'add' | 'edit';
  initial?: BankAccount;
  onClose: () => void;
}

function BankFormModal({ mode, initial, onClose }: BankFormModalProps) {
  const companyId = useActiveCompanyId();
  const createBank = useCreateBankAccount(companyId);
  const updateBank = useUpdateBankAccount(companyId);

  const [code, setCode] = useState(initial?.code ?? '');
  const [bank, setBank] = useState(initial?.bank ?? '');
  const [accNo, setAccNo] = useState(initial?.accNo ?? '');
  const [nameTh, setNameTh] = useState(initial?.nameTh ?? '');
  const [type, setType] = useState<BankAccountType>(initial?.type ?? 'CURRENT');
  const [branch, setBranch] = useState(initial?.branch ?? '');
  const [balance, setBalance] = useState(initial ? String(toNumber(initial.balance)) : '0');
  const [glAccountCode, setGlAccountCode] = useState(initial?.glAccountCode ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  // Cash/bank GL accounts of THIS company to pick from (ASSET on the cash-
  // equivalents fsLine, active, non-control) — mirrors the API validation.
  const { data: coa = [] } = useCoa(companyId);
  const cashAccounts = coa.filter(
    (a) =>
      a.type === 'ASSET' &&
      a.isActive &&
      !a.isControl &&
      a.fsLine === 'เงินสดและรายการเทียบเท่าเงินสด',
  );

  const submitting = createBank.isPending || updateBank.isPending;
  const balanceNum = Number(String(balance).replace(/,/g, ''));
  const disabled =
    !code ||
    !bank ||
    !accNo ||
    !nameTh ||
    !glAccountCode ||
    !Number.isFinite(balanceNum) ||
    balanceNum < 0 ||
    submitting;

  async function handleSubmit() {
    setError(null);
    try {
      if (mode === 'edit') {
        if (!initial) {
          setError('ไม่พบข้อมูลบัญชีธนาคาร');
          return;
        }
        // code is immutable on edit (natural key) — omit it from the partial PATCH.
        const data: UpdateBankAccount = {
          bank,
          accNo,
          nameTh,
          type,
          branch: branch || null,
          glAccountCode: glAccountCode || null,
          balance: balanceNum,
          isActive,
        };
        await updateBank.mutateAsync({ id: initial.id, data });
      } else {
        const input: Omit<CreateBankAccount, 'companyId'> = {
          code,
          bank,
          accNo,
          nameTh,
          type,
          branch: branch || null,
          glAccountCode: glAccountCode || null,
          balance: balanceNum,
          isActive,
        };
        await createBank.mutateAsync(input);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <div style={editModalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={editModalBox}>
        <div style={editModalHeader}>
          <h3 style={editModalTitle}>
            {mode === 'edit' ? 'แก้ไขบัญชีธนาคาร' : 'เพิ่มบัญชีธนาคาร'}
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={editModalBody}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>รหัสบัญชี</label>
              <input
                className="input code"
                aria-label="รหัสบัญชีธนาคาร"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={mode === 'edit'}
                placeholder="BA-001"
                style={{
                  width: '100%',
                  height: 38,
                  fontSize: 14,
                  background: mode === 'edit' ? '#fafafa' : undefined,
                }}
              />
            </div>
            <div>
              <label style={editModalLabel}>ธนาคาร</label>
              <input
                className="input"
                aria-label="ธนาคาร"
                value={bank}
                onChange={(e) => setBank(e.target.value)}
                placeholder="KBANK"
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>เลขที่บัญชี</label>
              <input
                className="input code"
                aria-label="เลขที่บัญชี"
                value={accNo}
                onChange={(e) => setAccNo(e.target.value)}
                placeholder="123-4-56789-0"
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={editModalLabel}>ประเภทบัญชี</label>
              <select
                className="select"
                aria-label="ประเภทบัญชี"
                value={type}
                onChange={(e) => setType(e.target.value as BankAccountType)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              >
                <option value="CURRENT">กระแสรายวัน (Current)</option>
                <option value="SAVINGS">ออมทรัพย์ (Savings)</option>
                <option value="FIXED">ฝากประจำ (Fixed)</option>
                <option value="FOREIGN">เงินตราต่างประเทศ (Foreign)</option>
              </select>
            </div>
          </div>
          <div>
            <label style={editModalLabel}>ชื่อบัญชี</label>
            <input
              className="input"
              aria-label="ชื่อบัญชี"
              value={nameTh}
              onChange={(e) => setNameTh(e.target.value)}
              placeholder="บัญชีหลัก — KBANK"
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          <div>
            <label style={editModalLabel}>บัญชีแยกประเภท GL (เงินฝากธนาคาร) *</label>
            <select
              className="select"
              aria-label="บัญชีแยกประเภท GL"
              value={glAccountCode}
              onChange={(e) => setGlAccountCode(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            >
              <option value="">— เลือกบัญชี GL เงินฝากธนาคาร —</option>
              {cashAccounts.map((a) => (
                <option key={a.code} value={a.code}>
                  {a.code} — {a.nameTh}
                </option>
              ))}
            </select>
            <div style={{ fontSize: 12, color: 'var(--text-muted)', marginTop: 4 }}>
              ผูกบัญชีธนาคารนี้กับรหัสบัญชีในผังบัญชี — เพื่อให้รายการที่ผ่านบัญชีนี้ลง GL ถูกบัญชี
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>สาขา (ถ้ามี)</label>
              <input
                className="input"
                aria-label="สาขา"
                value={branch}
                onChange={(e) => setBranch(e.target.value)}
                placeholder="สีลม"
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
            <div>
              <label style={editModalLabel}>ยอดยกมา (บาท)</label>
              <input
                type="number"
                className="input tnum"
                aria-label="ยอดยกมา"
                value={balance}
                onChange={(e) => setBalance(e.target.value)}
                min={0}
                step="0.01"
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            ใช้งานอยู่
          </label>
          {error && <div style={editModalError}>บันทึกไม่สำเร็จ: {error}</div>}
        </div>
        <div style={editModalFooter}>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={disabled}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// LoanFormModal — full company-loan create/edit form (Phase 42 follow-up).
// Replaces the generic 2-field modal which couldn't capture principal / rate /
// purpose / dates. Also carries the branch (สาขา) + cost-center (หน่วยงาน)
// dimensions so future loan-interest JVs inherit them.
// ============================================================

interface LoanFormModalProps {
  mode: 'add' | 'edit';
  initial?: Loan;
  onClose: () => void;
}

function LoanFormModal({ mode, initial, onClose }: LoanFormModalProps) {
  const companyId = useActiveCompanyId();
  const createLoan = useCreateLoan(companyId);
  const updateLoan = useUpdateLoan(companyId);
  const { data: projects = [] } = useProjects(companyId);

  const [code, setCode] = useState(initial?.code ?? '');
  const [bank, setBank] = useState(initial?.bank ?? '');
  const [principal, setPrincipal] = useState(initial ? String(toNumber(initial.principal)) : '');
  const [rate, setRate] = useState(initial ? String(toNumber(initial.rate)) : '');
  const [purpose, setPurpose] = useState<LoanPurpose>(initial?.purpose ?? 'GENERAL');
  const [tiedProjectId, setTiedProjectId] = useState(initial?.tiedProjectId ?? '');
  const [startDate, setStartDate] = useState(
    initial ? String(initial.startDate).slice(0, 10) : new Date().toISOString().slice(0, 10),
  );
  const [endDate, setEndDate] = useState(
    initial ? String(initial.endDate).slice(0, 10) : new Date().toISOString().slice(0, 10),
  );
  const [branchId, setBranchId] = useState(initial?.branchId ?? '');
  const [departmentId, setDepartmentId] = useState(initial?.departmentId ?? '');
  const [isActive, setIsActive] = useState(initial?.isActive ?? true);
  const [error, setError] = useState<string | null>(null);

  const submitting = createLoan.isPending || updateLoan.isPending;
  const principalNum = Number(principal.replace(/,/g, ''));
  const rateNum = Number(rate);
  const disabled =
    !code || !bank || !Number.isFinite(principalNum) || principalNum <= 0 || submitting;

  async function handleSubmit() {
    setError(null);
    try {
      if (mode === 'edit') {
        if (!initial) {
          setError('ไม่พบข้อมูลเงินกู้');
          return;
        }
        const data: UpdateLoan = {
          bank,
          principal: principalNum,
          rate: rateNum,
          purpose,
          tiedProjectId: tiedProjectId || null,
          startDate,
          endDate,
          branchId: branchId || null,
          departmentId: departmentId || null,
          isActive,
        };
        await updateLoan.mutateAsync({ id: initial.id, data });
      } else {
        const input: Omit<CreateLoan, 'companyId'> = {
          code,
          bank,
          principal: principalNum,
          rate: rateNum,
          purpose,
          tiedProjectId: tiedProjectId || null,
          startDate,
          endDate,
          branchId: branchId || null,
          departmentId: departmentId || null,
          isActive,
        };
        await createLoan.mutateAsync(input);
      }
      onClose();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'บันทึกไม่สำเร็จ');
    }
  }

  return (
    <div style={editModalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={editModalBox}>
        <div style={editModalHeader}>
          <h3 style={editModalTitle}>
            {mode === 'edit' ? 'แก้ไขเงินกู้บริษัท' : 'เพิ่มเงินกู้บริษัท'}
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={editModalBody}>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>รหัสเงินกู้</label>
              <input
                className="input code"
                value={code}
                onChange={(e) => setCode(e.target.value)}
                disabled={mode === 'edit'}
                placeholder="LN-2026-001"
              />
            </div>
            <div>
              <label style={editModalLabel}>ธนาคาร / ผู้ให้กู้</label>
              <input
                className="input"
                value={bank}
                onChange={(e) => setBank(e.target.value)}
                placeholder="KBANK"
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>เงินต้น (บาท)</label>
              <input
                type="number"
                className="input tnum"
                value={principal}
                onChange={(e) => setPrincipal(e.target.value)}
                min={0}
                step="0.01"
                style={{ textAlign: 'right' }}
              />
            </div>
            <div>
              <label style={editModalLabel}>อัตราดอกเบี้ย (%/ปี)</label>
              <input
                type="number"
                className="input tnum"
                value={rate}
                onChange={(e) => setRate(e.target.value)}
                min={0}
                step="0.0001"
                style={{ textAlign: 'right' }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>วัตถุประสงค์</label>
              <select
                className="select"
                value={purpose}
                onChange={(e) => setPurpose(e.target.value as LoanPurpose)}
                style={{ width: '100%' }}
              >
                <option value="GENERAL">ทั่วไป (General)</option>
                <option value="SPECIFIC">เฉพาะโครงการ (Specific)</option>
              </select>
            </div>
            <div>
              <label style={editModalLabel}>ผูกกับโครงการ (ถ้ามี)</label>
              <select
                className="select"
                value={tiedProjectId}
                onChange={(e) => setTiedProjectId(e.target.value)}
                style={{ width: '100%' }}
              >
                <option value="">— ไม่ผูก —</option>
                {projects.map((p) =>
                  p.id ? (
                    <option key={p.id} value={p.id}>
                      {p.code} — {p.nameTh}
                    </option>
                  ) : null,
                )}
              </select>
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>สาขา</label>
              <BranchSelect companyId={companyId} value={branchId} onChange={setBranchId} />
            </div>
            <div>
              <label style={editModalLabel}>หน่วยงาน</label>
              <DepartmentSelect
                companyId={companyId}
                value={departmentId}
                onChange={setDepartmentId}
                branchId={branchId}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>วันเริ่ม</label>
              <input
                type="date"
                className="input"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
              />
            </div>
            <div>
              <label style={editModalLabel}>วันครบกำหนด</label>
              <input
                type="date"
                className="input"
                value={endDate}
                min={startDate}
                onChange={(e) => setEndDate(e.target.value)}
              />
            </div>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            ใช้งาน
          </label>
          {error && <div style={editModalError}>บันทึกไม่สำเร็จ: {error}</div>}
        </div>
        <div style={editModalFooter}>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={disabled}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="check" size={14} /> {submitting ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// Rich edit modals for masters whose schemas couldn't fit the
// generic AddMasterModal (DocumentType, Period, VoucherBook).
// Each one owns its own form state + mutation; the parent only
// supplies the row + onClose hook.
// ============================================================

const DOC_FORMAT_OPTIONS: Readonly<Record<DocumentFormat, string>> = {
  MONTHLY: 'รายเดือน',
  YEARLY_MONTH: 'รายปี (YYMM)',
  YEARLY_RUNNING: 'รายปี',
  LIFETIME: 'ตลอดอายุ',
};

const RESET_RULE_OPTIONS: Readonly<Record<ResetRule, string>> = {
  MONTHLY: 'รีเซ็ตรายเดือน',
  YEARLY: 'รีเซ็ตรายปี',
  NEVER: 'ไม่รีเซ็ต',
};

const PERIOD_STATUS_OPTIONS: Readonly<Record<PeriodStatus, string>> = {
  CLOSED: 'ปิดงวด',
  OPEN: 'เปิดอยู่',
  FUTURE: 'ยังไม่เปิด',
};

// Free-text on the server (Prisma column); we offer the 6 known options + 'manual'
// fallback so editors don't have to memorize the rule strings.
const ROUTING_RULES: readonly string[] = [
  'auto-branch',
  'auto-dept',
  'auto-transaction',
  'auto-customer',
  'manual',
  'hybrid',
];

const editModalOverlay: CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15,15,20,0.45)',
  display: 'grid',
  placeItems: 'center',
  zIndex: 150,
  padding: 24,
};

const editModalBox: CSSProperties = {
  background: 'white',
  borderRadius: 12,
  width: 560,
  maxWidth: '100%',
  boxShadow: '0 28px 80px rgba(0,0,0,0.18)',
  overflow: 'hidden',
};

const editModalHeader: CSSProperties = {
  padding: '18px 22px',
  borderBottom: '1px solid var(--border)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
};

const editModalTitle: CSSProperties = {
  margin: 0,
  fontSize: 18,
  fontWeight: 600,
  fontFamily: 'var(--font-display)',
};

const editModalBody: CSSProperties = {
  padding: '22px',
  display: 'flex',
  flexDirection: 'column',
  gap: 12,
};

const editModalFooter: CSSProperties = {
  padding: '14px 22px',
  borderTop: '1px solid var(--border)',
  display: 'flex',
  justifyContent: 'flex-end',
  gap: 8,
  background: '#fafafa',
};

const editModalLabel: CSSProperties = {
  fontSize: 14,
  color: 'var(--text-muted)',
  fontWeight: 500,
  display: 'block',
  marginBottom: 6,
};

const editModalError: CSSProperties = {
  color: 'var(--negative)',
  fontSize: 14,
  marginTop: 8,
};

// '2026-04-30T12:34:56.000Z' → '2026-04-30T12:34' (local time slot for the
// <input type="datetime-local"> control). Returns '' when the source is null.
function isoToLocalInput(iso: string | null | undefined): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

function localInputToIso(local: string): string | null {
  if (!local) return null;
  const d = new Date(local);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

interface EditDocumentTypeModalProps {
  docType: DocumentType;
  companyId: string | undefined;
  onClose: () => void;
}

function EditDocumentTypeModal({ docType, companyId, onClose }: EditDocumentTypeModalProps) {
  const [nameTh, setNameTh] = useState(docType.nameTh);
  const [format, setFormat] = useState<DocumentFormat>(docType.format);
  const [resetRule, setResetRule] = useState<ResetRule>(docType.resetRule);
  const [padWidth, setPadWidth] = useState<number>(docType.padWidth);
  const [isActive, setIsActive] = useState<boolean>(docType.isActive);
  const update = useUpdateDocumentType(companyId);

  async function handleSubmit() {
    const data: UpdateDocumentType = { nameTh, format, resetRule, padWidth, isActive };
    await update.mutateAsync({ id: docType.id, data });
    onClose();
  }

  const disabled = !nameTh || padWidth < 1 || padWidth > 10 || update.isPending;

  return (
    <div style={editModalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={editModalBox}>
        <div style={editModalHeader}>
          <h3 style={editModalTitle}>แก้ไขประเภทเอกสาร — {docType.prefix}</h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={editModalBody}>
          <div>
            <label style={editModalLabel}>Prefix</label>
            <input
              className="input code"
              value={docType.prefix}
              readOnly
              style={{ width: '100%', height: 38, fontSize: 14, background: '#fafafa' }}
            />
          </div>
          <div>
            <label style={editModalLabel}>ชื่อเอกสาร</label>
            <input
              className="input"
              value={nameTh}
              onChange={(e) => setNameTh(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>รูปแบบ</label>
              <select
                className="select"
                value={format}
                onChange={(e) => setFormat(e.target.value as DocumentFormat)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              >
                {(Object.keys(DOC_FORMAT_OPTIONS) as DocumentFormat[]).map((k) => (
                  <option key={k} value={k}>
                    {DOC_FORMAT_OPTIONS[k]}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label style={editModalLabel}>การ Reset</label>
              <select
                className="select"
                value={resetRule}
                onChange={(e) => setResetRule(e.target.value as ResetRule)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              >
                {(Object.keys(RESET_RULE_OPTIONS) as ResetRule[]).map((k) => (
                  <option key={k} value={k}>
                    {RESET_RULE_OPTIONS[k]}
                  </option>
                ))}
              </select>
            </div>
          </div>
          <div
            style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, alignItems: 'end' }}
          >
            <div>
              <label style={editModalLabel}>Pad Width</label>
              <input
                className="input tnum"
                type="number"
                min={1}
                max={10}
                value={padWidth}
                onChange={(e) => setPadWidth(Number(e.target.value))}
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
            <label
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                fontSize: 14,
                paddingBottom: 8,
              }}
            >
              <input
                type="checkbox"
                checked={isActive}
                onChange={(e) => setIsActive(e.target.checked)}
              />
              ใช้งานอยู่
            </label>
          </div>
          {update.error && (
            <div style={editModalError}>บันทึกไม่สำเร็จ: {update.error.message}</div>
          )}
        </div>
        <div style={editModalFooter}>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={disabled}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="check" size={14} /> {update.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface EditPeriodModalProps {
  period: PeriodMaster;
  companyId: string | undefined;
  onClose: () => void;
}

function EditPeriodModal({ period, companyId, onClose }: EditPeriodModalProps) {
  const [nameTh, setNameTh] = useState(period.nameTh);
  const [status, setStatus] = useState<PeriodStatus>(period.status);
  const [closedBy, setClosedBy] = useState(period.closedBy ?? '');
  const [closedDate, setClosedDate] = useState(isoToLocalInput(period.closedDate));
  const update = useUpdatePeriod(companyId);

  async function handleSubmit() {
    // closedBy/closedDate get cleared on transitions away from CLOSED so the
    // DB doesn't keep stale audit fields on a reopened period.
    const data: UpdatePeriodMaster = {
      nameTh,
      status,
      closedBy: status === 'CLOSED' ? closedBy || null : null,
      closedDate: status === 'CLOSED' ? localInputToIso(closedDate) : null,
    };
    await update.mutateAsync({ id: period.id, data });
    onClose();
  }

  const disabled = !nameTh || update.isPending;

  return (
    <div style={editModalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={editModalBox}>
        <div style={editModalHeader}>
          <h3 style={editModalTitle}>แก้ไขงวด — {periodCodeToDisplay(period.period)}</h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={editModalBody}>
          <div>
            <label style={editModalLabel}>งวด (YYYYMM)</label>
            <input
              className="input code"
              value={period.period}
              readOnly
              style={{ width: '100%', height: 38, fontSize: 14, background: '#fafafa' }}
            />
          </div>
          <div>
            <label style={editModalLabel}>ชื่อ</label>
            <input
              className="input"
              value={nameTh}
              onChange={(e) => setNameTh(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          <div>
            <label style={editModalLabel}>สถานะ</label>
            <select
              className="select"
              value={status}
              onChange={(e) => setStatus(e.target.value as PeriodStatus)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            >
              {(Object.keys(PERIOD_STATUS_OPTIONS) as PeriodStatus[]).map((k) => (
                <option key={k} value={k}>
                  {PERIOD_STATUS_OPTIONS[k]}
                </option>
              ))}
            </select>
          </div>
          {status === 'CLOSED' && (
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
              <div>
                <label style={editModalLabel}>ผู้ปิดงวด</label>
                <input
                  className="input"
                  value={closedBy}
                  onChange={(e) => setClosedBy(e.target.value)}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                />
              </div>
              <div>
                <label style={editModalLabel}>วันที่ปิดงวด</label>
                <input
                  className="input"
                  type="datetime-local"
                  value={closedDate}
                  onChange={(e) => setClosedDate(e.target.value)}
                  style={{ width: '100%', height: 38, fontSize: 14 }}
                />
              </div>
            </div>
          )}
          {update.error && (
            <div style={editModalError}>บันทึกไม่สำเร็จ: {update.error.message}</div>
          )}
        </div>
        <div style={editModalFooter}>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={disabled}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="check" size={14} /> {update.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  );
}

interface EditVoucherBookModalProps {
  book: VoucherBookWithCounter;
  docType: DocumentType;
  companyId: string | undefined;
  onClose: () => void;
}

function EditVoucherBookModal({ book, docType, companyId, onClose }: EditVoucherBookModalProps) {
  const [nameTh, setNameTh] = useState(book.nameTh);
  const [rangeStart, setRangeStart] = useState(book.rangeStart);
  const [rangeEnd, setRangeEnd] = useState(book.rangeEnd);
  const [team, setTeam] = useState(book.team ?? '');
  const [routingRule, setRoutingRule] = useState(book.routingRule ?? 'manual');
  const [isActive, setIsActive] = useState(book.isActive);
  const update = useUpdateVoucherBook(companyId, docType.id);

  async function handleSubmit() {
    const data: UpdateVoucherBook = {
      nameTh,
      rangeStart,
      rangeEnd,
      team: team || null,
      routingRule: routingRule || null,
      isActive,
    };
    await update.mutateAsync({ bookId: book.id, data });
    onClose();
  }

  const disabled = !nameTh || rangeEnd <= rangeStart || update.isPending;

  return (
    <div style={editModalOverlay} onClick={onClose}>
      <div onClick={(e) => e.stopPropagation()} style={editModalBox}>
        <div style={editModalHeader}>
          <h3 style={editModalTitle}>
            แก้ไขเล่ม — {docType.prefix} Book {book.bookCode}
          </h3>
          <button className="btn ghost" onClick={onClose} style={{ padding: '0 8px' }}>
            <Icon name="x" size={18} />
          </button>
        </div>
        <div style={editModalBody}>
          <div style={{ display: 'grid', gridTemplateColumns: '120px 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>รหัสเล่ม</label>
              <input
                className="input code"
                value={book.bookCode}
                readOnly
                style={{
                  width: '100%',
                  height: 38,
                  fontSize: 14,
                  background: '#fafafa',
                  textAlign: 'center',
                }}
              />
            </div>
            <div>
              <label style={editModalLabel}>ชื่อเล่ม</label>
              <input
                className="input"
                value={nameTh}
                onChange={(e) => setNameTh(e.target.value)}
                style={{ width: '100%', height: 38, fontSize: 14 }}
              />
            </div>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <div>
              <label style={editModalLabel}>Range เริ่ม</label>
              <input
                className="input code tnum"
                type="number"
                value={rangeStart}
                onChange={(e) => setRangeStart(Number(e.target.value))}
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
            <div>
              <label style={editModalLabel}>Range สิ้นสุด</label>
              <input
                className="input code tnum"
                type="number"
                value={rangeEnd}
                onChange={(e) => setRangeEnd(Number(e.target.value))}
                style={{ width: '100%', height: 38, fontSize: 14, textAlign: 'right' }}
              />
            </div>
          </div>
          <div>
            <label style={editModalLabel}>ทีม/หน่วยงาน</label>
            <input
              className="input"
              value={team}
              onChange={(e) => setTeam(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            />
          </div>
          <div>
            <label style={editModalLabel}>Routing Rule</label>
            <select
              className="select"
              value={routingRule}
              onChange={(e) => setRoutingRule(e.target.value)}
              style={{ width: '100%', height: 38, fontSize: 14 }}
            >
              {ROUTING_RULES.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </div>
          <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 14 }}>
            <input
              type="checkbox"
              checked={isActive}
              onChange={(e) => setIsActive(e.target.checked)}
            />
            ใช้งานอยู่
          </label>
          {update.error && (
            <div style={editModalError}>บันทึกไม่สำเร็จ: {update.error.message}</div>
          )}
        </div>
        <div style={editModalFooter}>
          <button className="btn ghost" onClick={onClose}>
            ยกเลิก
          </button>
          <button
            className="btn primary"
            disabled={disabled}
            onClick={() => {
              void handleSubmit();
            }}
          >
            <Icon name="check" size={14} /> {update.isPending ? 'กำลังบันทึก…' : 'บันทึก'}
          </button>
        </div>
      </div>
    </div>
  );
}
